// frontend/src/components/FrontendPreview.tsx
import { useRef, useEffect, useState, useCallback } from 'react';
import { Play, Maximize2, Minimize2, RefreshCw, AlertTriangle, X, Copy, Check, Loader2, ChevronDown, ChevronUp, Terminal } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { getStoredSession } from '../lib/api';
import { getErrorCatalogJS, ERROR_CODES, type PlatformError } from '../lib/platformErrors';
import { saveCachedRun, loadCachedRun, clearCachedRun } from '../lib/outputFileCache';

// Cutoff date: mini-apps updated before this used legacy body layout (flex centering, padding)
const LEGACY_BODY_CUTOFF = '2026-02-25T00:00:00Z';

// Fetch common CSS from Supabase function (single source of truth)
// Always fetch fresh to ensure theme updates are applied
async function getCommonCss(projectUpdatedAt?: string | null): Promise<string> {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const legacyBody = projectUpdatedAt && projectUpdatedAt < LEGACY_BODY_CUTOFF ? '&legacy_body=1' : '';
  const response = await fetch(`${supabaseUrl}/functions/v1/common-css?v=${Date.now()}${legacyBody}`);
  return await response.text();
}

interface FormDataEntry {
  key: string;
  type: 'file' | 'string';
  filename?: string;
  contentType?: string;
  data: number[] | string;
}

interface RunLogs {
  stdout: string;
  stderr: string;
  exitCode: number;
  hasAppError?: boolean; // True if application-level error was detected in output
}

interface StreamingProgress {
  percent: number;
  message: string;
}

interface StreamingLogs {
  stdout: string;
  stderr: string;
}

// Embedding batch limits by tier (texts per request) — must match generate-embeddings/index.ts
export const EMBEDDING_BATCH_LIMITS: Record<string, number> = {
  free: 50,
  standard_with_ads: 100,
  standard: 100,
  premium: 1000,
};

// Max combined mini-app submit payload by tier (bytes) — must match miniapp-auth/index.ts
export const MINIAPP_MAX_PAYLOAD_BYTES: Record<string, number> = {
  anonymous: 10 * 1024 * 1024,
  free: 10 * 1024 * 1024,
  standard_with_ads: 20 * 1024 * 1024,
  standard: 20 * 1024 * 1024,
  premium: 64 * 1024 * 1024,
};

// User context passed to mini-apps via window.llambada.user
export interface MiniAppUserContext {
  id: string | null;
  email: string | null;
  displayName: string | null;
  tier: 'free' | 'standard_with_ads' | 'standard' | 'premium';
  locale: string;  // UI locale chosen by the user (e.g. 'en', 'fr', 'zh', 'ru')
  creditsAvailable: number;
  creditsLoaded: boolean;
  creditCosts: Record<string, number>;  // model alias → credits per call (e.g. { fast: 4, imagegen: 8 })
  maxPayloadBytes: number;  // max llambada.submit payload size for current user's tier
  embeddingBatchLimit: number;  // max texts per embedding request for user's tier
}

export interface MiniAppFamilyConfig {
  params: Record<string, string>;
  description: string | null;
  title: string | null;  // interpolated title for the iframe's document.title (from name_template/title_template)
}

interface FrontendPreviewProps {
  html: string;
  frontendFiles?: Record<string, string>;
  onSubmit: (entries: FormDataEntry[]) => Promise<void>;
  isProcessing?: boolean;
  isComplete?: boolean;
  backendError?: string | null;
  buildStatus?: string | null;
  // Logs panel props
  runLogs?: RunLogs | null;
  runStatus?: 'idle' | 'authenticating' | 'running' | 'completed' | 'failed';
  runProgress?: StreamingProgress | null;
  streamingLogs?: StreamingLogs | null;
  buildLogs?: string | null;
  buildLogsLoading?: boolean;
  buildLogsError?: string | null;
  buildLogsDiagnostics?: {
    steps: string[];
    logsBucket?: string | null;
    gcsAttempted: boolean;
    gcsSuccess: boolean;
    gcsError?: string;
    cloudLoggingAttempted: boolean;
    cloudLoggingSuccess: boolean;
    cloudLoggingError?: string;
  } | null;
  isImageSizeLimitError?: boolean;
  lastBuildAt?: string | null;
  lastBuildId?: string | null;
  onFetchBuildLogs?: () => void;
  onTriggerRebuild?: () => void;
  // Public mode - hides developer tools and shows only the mini-app
  publicMode?: boolean;
  // Project ID for data storage API
  projectId?: string;
  // User context for mini-app (id, email, displayName, tier)
  userContext?: MiniAppUserContext;
  // Fixed family parameters for family-generated forks
  familyConfig?: MiniAppFamilyConfig | null;
  // Callback when a frontend JS error occurs - reports formatted error string
  onFrontendError?: (error: string) => void;
  // Flag to block the preview (e.g., during container rebuild)
  isBlocked?: boolean;
  // Message to show when blocked
  blockedMessage?: string;
  // Canonical URL of this mini-app (for share links). If published, should be the public URL.
  appUrl?: string;
  // Current session access token - pushed proactively to iframe so it always has a fresh token
  sessionToken?: string | null;
  // Async callback to get a guaranteed-fresh session token (calls refresh if needed)
  getSessionToken?: () => Promise<string | null>;
  // Async callback to get a Turnstile token for guest-protected API calls
  getTurnstileToken?: () => Promise<string | null>;
  // Ref to the outermost bordered card container, used for height measurement
  containerRef?: React.RefObject<HTMLDivElement>;
  // Project's last updated timestamp — used to decide whether to include legacy body layout CSS
  projectUpdatedAt?: string | null;
}

interface CapturedError {
  type: 'error' | 'unhandledrejection';
  message: string;
  stack?: string;
  source?: string;
  lineno?: number;
  colno?: number;
  timestamp: number;
}

type HostToastTone = '' | 'success' | 'error' | 'warning';

interface HostToastState {
  message: string;
  tone: HostToastTone;
}

// The Llambada runtime that gets injected into the iframe
// Takes frontendFiles as parameter to embed them directly in the script
function getLlambadaRuntime(
  frontendFiles: Record<string, string> = {},
  supabaseUrl: string = '',
  projectId: string = '',
  userContext: MiniAppUserContext = {
    id: null,
    email: null,
    displayName: null,
    tier: 'free',
    locale: 'en',
    creditsAvailable: 0,
    creditsLoaded: false,
    creditCosts: {},
    maxPayloadBytes: MINIAPP_MAX_PAYLOAD_BYTES.free,
    embeddingBatchLimit: 50,
  },
  familyConfig: MiniAppFamilyConfig | null = null,
  appUrl: string = '',
  urlParams: Record<string, string> = {},
  publicMode: boolean = false,
  initialAuthToken: string | null = null
): string {
  // Escape </script> sequences in JSON to prevent premature script tag closure
  // when embedded HTML files (e.g., backup files) contain </script> tags.
  const escapeForScript = (s: string) => s.replace(/<\//g, '<\\/');
  const filesJson = escapeForScript(JSON.stringify(frontendFiles));
  const safeSupabaseUrl = JSON.stringify(supabaseUrl);
  const safeProjectId = JSON.stringify(projectId);
  const safeUserContext = escapeForScript(JSON.stringify(userContext));
  const safeFamilyConfig = escapeForScript(JSON.stringify(familyConfig));
  const safeAppUrl = escapeForScript(JSON.stringify(appUrl));
  const safeUrlParams = escapeForScript(JSON.stringify(urlParams));
  const safeInitialAuthToken = escapeForScript(JSON.stringify(initialAuthToken));
  const errorCatalogJS = getErrorCatalogJS();
  
  return `
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/simple-icons-font@v16/font/simple-icons.min.css">
<script src="https://unpkg.com/lucide@0.460.0/dist/umd/lucide.min.js"></script>
<script>
console.log('[Llambada:Lucide] Lucide library loaded, typeof lucide:', typeof lucide);

// Expose refresh function that checks if lucide is available
window.llambadaRefreshIcons = function() {
  if (typeof lucide !== 'undefined' && lucide.createIcons) {
    lucide.createIcons();
  }
};

// Auto-initialize icons on DOMContentLoaded
document.addEventListener('DOMContentLoaded', function() {
  if (typeof lucide !== 'undefined' && lucide.createIcons) {
    lucide.createIcons();
    console.log('[Llambada:Lucide] Icons initialized on DOMContentLoaded');
  }
});
</script>
<script>
// Platform-level file input handling:
// 1. Validate file sizes against tier limits and alert the user immediately.
// 2. Auto-reset file inputs after change event so the same file can be re-selected.
(function() {
  document.addEventListener('change', function(e) {
    if (e.target && e.target.type === 'file') {
      // Check file sizes against tier limit
      var files = e.target.files;
      if (files && files.length > 0 && window.llambada && window.llambada.user) {
        var maxBytes = Number(window.llambada.user.maxPayloadBytes);
        if (!Number.isFinite(maxBytes) || maxBytes <= 0) {
          return;
        }
        var maxMB = Math.round(maxBytes / (1024 * 1024));
        var totalSize = 0;
        for (var i = 0; i < files.length; i++) {
          totalSize += files[i].size;
        }
        if (totalSize > maxBytes) {
          var sizeMB = (totalSize / (1024 * 1024)).toFixed(1);
          var msg = 'Selected file' + (files.length > 1 ? 's are' : ' is') + ' too large (' + sizeMB + ' MB). Your plan allows up to ' + maxMB + ' MB.';
          if (window.llambada.showToast) {
            window.llambada.showToast(msg, 'error');
          }
        }
      }
      // Auto-reset so the same file can be re-selected.
      // Uses setTimeout so the mini-app's own change handler runs first.
      setTimeout(function() { e.target.value = ''; }, 0);
    }
  }, true);
})();
</script>
<script>
(function() {
  let isBackendBuilding = false;

  // In-memory file storage (replaces OPFS - no allow-same-origin needed)
  const __fileStore = new Map();

  // Decode a base64 string to Uint8Array
  function __base64ToBytes(b64) {
    var bin = atob(b64);
    var bytes = new Uint8Array(bin.length);
    for (var i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return bytes;
  }

  // Guess MIME type for binary files
  function __binaryMimeType(path) {
    var ext = path.split('.').pop().toLowerCase();
    var map = { png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif', webp: 'image/webp', bmp: 'image/bmp', ico: 'image/x-icon', svg: 'image/svg+xml', tiff: 'image/tiff', tif: 'image/tiff', pdf: 'application/pdf', woff: 'font/woff', woff2: 'font/woff2', ttf: 'font/ttf', otf: 'font/otf' };
    return map[ext] || 'application/octet-stream';
  }

  // Frontend files embedded at build time
  const __frontendFiles = ${filesJson};
  console.log('[Llambada] Embedded frontend files:', Object.keys(__frontendFiles));
  for (const [name, content] of Object.entries(__frontendFiles)) {
    // Binary files arrive with a "base64:" prefix — decode to Uint8Array
    if (typeof content === 'string' && content.startsWith('base64:')) {
      __fileStore.set(name, __base64ToBytes(content.slice(7)));
    } else {
      __fileStore.set(name, content);
    }
  }

  // Intercept fetch() so mini-apps can fetch frontend files by relative path
  // (e.g., fetch('synonyms.json') or fetch('./data/config.json'))
  const __originalFetch = window.fetch.bind(window);
  window.fetch = function(input, init) {
    let url = (typeof input === 'string') ? input : (input instanceof Request ? input.url : String(input));
    // Strip query params and hash for matching, normalize relative paths
    let cleanPath = url.split('?')[0].split('#')[0];
    if (cleanPath.startsWith('./')) cleanPath = cleanPath.substring(2);
    // Only intercept relative paths (no protocol, no leading //)
    if (!cleanPath.includes('://') && !cleanPath.startsWith('//')) {
      if (__fileStore.has(cleanPath)) {
        console.log('[Llambada] Serving from embedded files:', cleanPath);
        const stored = __fileStore.get(cleanPath);
        if (stored instanceof Uint8Array) {
          return Promise.resolve(new Response(stored, { status: 200, headers: { 'Content-Type': __binaryMimeType(cleanPath) } }));
        }
        // Guess MIME type from extension
        const ext = cleanPath.split('.').pop().toLowerCase();
        const mimeTypes = { json: 'application/json', js: 'application/javascript', css: 'text/css', html: 'text/html', txt: 'text/plain', svg: 'image/svg+xml', xml: 'application/xml', csv: 'text/csv' };
        const contentType = mimeTypes[ext] || 'application/octet-stream';
        return Promise.resolve(new Response(stored, { status: 200, headers: { 'Content-Type': contentType } }));
      }
    }
    return __originalFetch(input, init);
  };

  // Platform configuration for data storage API
  window.__LLAMBADA_SUPABASE_URL = ${safeSupabaseUrl};
  window.__LLAMBADA_PROJECT_ID = ${safeProjectId};
  
  // User context injected by platform
  const __userContext = ${safeUserContext};
  const __familyConfig = ${safeFamilyConfig};
  
  // Error code catalog injected by platform (single source of truth)
  ${errorCatalogJS}
  
  // Project ID for OPFS namespacing - each project gets its own directory
  const __projectId = ${safeProjectId} || 'default';
  
  // App URL for constructing share links (since iframe runs in blob URL context)
  const __appUrl = ${safeAppUrl};
  
  // URL parameters from the parent page (since iframe can't access parent's location.search)
  const __urlParams = ${safeUrlParams};

  // Whether this is a published mini-app (true) or a private dev/test session (false)
  const __publicMode = ${publicMode};

  // Share URL compatibility shim:
  // Some mini-apps still build social share URLs with window.location.href (blob URL in iframe),
  // which breaks LinkedIn/Facebook share dialogs. Rewrite those to llambada.appUrl centrally.
  function sanitizeSharePopupUrl(rawUrl) {
    if (typeof rawUrl !== 'string') return rawUrl;
    const trimmed = rawUrl.trim();
    if (!trimmed) return rawUrl;

    const appUrl = (typeof __appUrl === 'string' ? __appUrl : '').trim();
    if (!appUrl) return rawUrl;

    let parsed;
    try {
      parsed = new URL(trimmed, window.location.href);
    } catch (_) {
      return rawUrl;
    }

    function shouldReplaceShareTarget(value) {
      if (!value) return true;
      try {
        const normalized = new URL(value, window.location.href);
        if (normalized.protocol === 'blob:') return true;
        if (normalized.href === window.location.href) return true;
        return false;
      } catch (_) {
        return value.indexOf('blob:') !== -1 || value === window.location.href;
      }
    }

    const host = parsed.hostname.toLowerCase().replace(/^www\./, '');
    const path = parsed.pathname.toLowerCase();

    if (host === 'linkedin.com' && (path === '/sharing/share-offsite' || path === '/sharing/share-offsite/')) {
      const currentUrl = parsed.searchParams.get('url');
      if (shouldReplaceShareTarget(currentUrl)) {
        parsed.searchParams.set('url', appUrl);
      }
      return parsed.toString();
    }

    if (host === 'facebook.com' && path === '/sharer/sharer.php') {
      const currentUrl = parsed.searchParams.get('u');
      if (shouldReplaceShareTarget(currentUrl)) {
        parsed.searchParams.set('u', appUrl);
      }
      return parsed.toString();
    }

    return rawUrl;
  }

  function isSocialSharePopupUrl(rawUrl) {
    if (typeof rawUrl !== 'string') return false;
    let parsed;
    try {
      parsed = new URL(rawUrl, window.location.href);
    } catch (_) {
      return false;
    }
    const host = parsed.hostname.toLowerCase().replace(/^www\./, '');
    const path = parsed.pathname.toLowerCase();
    if ((host === 'twitter.com' || host === 'x.com') && path === '/intent/tweet') return true;
    if (host === 'threads.net' && path === '/intent/post') return true;
    if (host === 'linkedin.com' && (path === '/sharing/share-offsite' || path === '/sharing/share-offsite/')) return true;
    if (host === 'facebook.com' && path === '/sharer/sharer.php') return true;
    return false;
  }

  function sanitizeSharePopupFeatures(rawUrl, rawFeatures) {
    if (typeof rawFeatures !== 'string') return rawFeatures;
    if (!isSocialSharePopupUrl(rawUrl)) return rawFeatures;
    const cleaned = rawFeatures
      .split(',')
      .map(token => token.trim())
      .filter(token => token.length > 0 && token.toLowerCase() !== 'noreferrer');
    return cleaned.join(',');
  }

  if (typeof window.open === 'function') {
    const nativeWindowOpen = window.open.bind(window);
    window.open = function(url, target, features) {
      const sanitizedUrl = sanitizeSharePopupUrl(url);
      const sanitizedFeatures = sanitizeSharePopupFeatures(sanitizedUrl, features);
      return nativeWindowOpen(sanitizedUrl, target, sanitizedFeatures);
    };
  }

  // No async init needed — in-memory store is ready synchronously

  // Error capturing
  window.onerror = function(message, source, lineno, colno, error) {
    // ResizeObserver loop errors are benign browser warnings, not real errors
    if (String(message).indexOf('ResizeObserver') !== -1) return false;
    window.parent.postMessage({
      type: 'llambada:jsError',
      errorType: 'error',
      message: String(message),
      stack: error?.stack || '',
      source: source,
      lineno: lineno,
      colno: colno,
      timestamp: Date.now()
    }, '*');
    return false;
  };

  window.onunhandledrejection = function(event) {
    const reason = event.reason;
    window.parent.postMessage({
      type: 'llambada:jsError',
      errorType: 'unhandledrejection',
      message: reason?.message || String(reason),
      stack: reason?.stack || '',
      timestamp: Date.now()
    }, '*');
  };

  // In-memory file helpers (replace OPFS — no allow-same-origin needed)
  window.readFile = async function(path) {
    const data = __fileStore.get(path);
    if (data === undefined) throw new Error('File not found: ' + path);
    if (data instanceof Uint8Array || data instanceof ArrayBuffer) return new Blob([data]);
    return new Blob([data]);
  };

  window.readFileText = async function(path) {
    const data = __fileStore.get(path);
    if (data === undefined) throw new Error('File not found: ' + path);
    if (typeof data === 'string') return data;
    if (data instanceof Blob) return await data.text();
    return new TextDecoder().decode(data);
  };

  window.listFiles = async function(dirPath) {
    const prefix = dirPath ? dirPath.replace(/\\/$/, '') + '/' : '';
    const results = [];
    for (const key of __fileStore.keys()) {
      if (!prefix || key.startsWith(prefix)) {
        const relative = prefix ? key.slice(prefix.length) : key;
        if (relative && !relative.includes('/')) {
          results.push({ name: relative, kind: 'file' });
        }
      }
    }
    return results;
  };

  window.__clearOutputFiles = async function() {
    const frontendFileNames = new Set(Object.keys(__frontendFiles));
    for (const key of __fileStore.keys()) {
      if (!frontendFileNames.has(key)) {
        __fileStore.delete(key);
        console.log('[Llambada] Cleared output file:', key);
      }
    }
    console.log('[Llambada] Output files cleared for new job');
  };

  window.writeFile = async function(path, data) {
    __fileStore.set(path, data);
  };

  // Intercept fetch to serve local files from in-memory store
  const originalFetch = window.fetch;
  function guessContentType(path) {
    if (path.endsWith('.json')) return 'application/json';
    if (path.endsWith('.txt')) return 'text/plain';
    if (path.endsWith('.html')) return 'text/html';
    if (path.endsWith('.css')) return 'text/css';
    if (path.endsWith('.js')) return 'application/javascript';
    if (path.endsWith('.svg')) return 'image/svg+xml';
    if (path.endsWith('.csv')) return 'text/csv';
    return 'application/octet-stream';
  }

  async function fetchFrontendFileFromProjectCode(path) {
    const supabaseUrl = window.__LLAMBADA_SUPABASE_URL || '';
    if (!supabaseUrl || !__projectId) return null;

    try {
      const requestUrl = new URL(supabaseUrl + '/functions/v1/project-code');
      requestUrl.searchParams.set('projectId', __projectId);
      requestUrl.searchParams.set('filename', 'frontend/' + path);
      requestUrl.searchParams.set('_t', String(Date.now()));

      const headers = {};
      if (!__publicMode) {
        const token = await getAuthToken();
        if (!token) return null;
        headers['Authorization'] = 'Bearer ' + token;
      }

      const response = await originalFetch.call(window, requestUrl.toString(), {
        method: 'GET',
        headers,
      });
      if (!response.ok) return null;

      const payload = await response.json().catch(() => null);
      if (!payload || typeof payload.content !== 'string') return null;

      const text = payload.content;

      // Binary files arrive with a "base64:" prefix — decode and serve as binary
      if (text.startsWith('base64:')) {
        const bytes = __base64ToBytes(text.slice(7));
        __fileStore.set(path, bytes);
        return new Response(bytes, {
          status: 200,
          headers: { 'Content-Type': __binaryMimeType(path) },
        });
      }

      __fileStore.set(path, text);

      return new Response(text, {
        status: 200,
        headers: { 'Content-Type': guessContentType(path) },
      });
    } catch (_err) {
      return null;
    }
  }

  window.fetch = async function(input, init) {
    const url = typeof input === 'string' ? input : input.url;
    console.log('[Llambada] fetch intercepted:', url);

    // Check if it's a relative URL (local file)
    if (url && !url.startsWith('http://') && !url.startsWith('https://') && !url.startsWith('//') && !url.startsWith('data:')) {
      // Normalize the path (remove leading ./ if present)
      const path = url.replace(/^\\.?\\//, '');
      console.log('[Llambada] Treating as local file, path:', path);

      if (__fileStore.has(path)) {
        try {
          const stored = __fileStore.get(path);
          if (stored instanceof Uint8Array) {
            console.log('[Llambada] Serving binary file from store, length:', stored.length);
            return new Response(stored, {
              status: 200,
              headers: { 'Content-Type': __binaryMimeType(path) }
            });
          }
          const file = await window.readFile(path);
          const text = await file.text();
          console.log('[Llambada] File read successfully, length:', text.length);

          return new Response(text, {
            status: 200,
            headers: { 'Content-Type': guessContentType(path) }
          });
        } catch (e) {
          console.log('[Llambada] File read error:', path, 'Error:', e.message);
        }
      }

      // Not in memory: try project-code on demand (supports large frontend assets).
      const fetched = await fetchFrontendFileFromProjectCode(path);
      if (fetched) {
        console.log('[Llambada] Served on-demand frontend file:', path);
        return fetched;
      }
    }

    return originalFetch.call(window, input, init);
  };

  // Convert FormData to transferable format
  async function serializeFormData(formData) {
    const entries = [];
    for (const [key, value] of formData.entries()) {
      if (value instanceof File) {
        const buffer = await value.arrayBuffer();
        entries.push({
          key,
          type: 'file',
          filename: value.name,
          contentType: value.type,
          data: Array.from(new Uint8Array(buffer))
        });
      } else {
        entries.push({
          key,
          type: 'string',
          data: value
        });
      }
    }
    return entries;
  }

  // Submit FormData to backend
  // Platform delivers each entry as a file in input/
  // Entry key becomes filename, entry value becomes content
  
  // Auth token handling for data storage API
  let __authToken = ${safeInitialAuthToken};
  let __authTokenPromise = null;
  let __authTokenResolve = null;
  let __turnstileTokenPromise = null;
  let __turnstileTokenResolve = null;
  const __supabaseUrl = window.__LLAMBADA_SUPABASE_URL || '';
  // Note: __projectId is already declared at the top of the runtime

  // Pending-request tracking: prevents data loss when user navigates away mid-request
  let __pendingRequests = 0;
  let __restoreToastSuppressUntil = 0;

  // After tab recovery, scan the DOM for broken media elements (images, videos,
  // audio) and restore them using cached input file data sent by the platform.
  function __restoreBrokenMedia(inputFileData) {
    try {
      // Separate input files by media type
      var imageInputs = [];
      var videoInputs = [];
      var audioInputs = [];
      for (var i = 0; i < inputFileData.length; i++) {
        var ct = inputFileData[i].contentType || '';
        if (ct.startsWith('image/')) imageInputs.push(inputFileData[i]);
        else if (ct.startsWith('video/')) videoInputs.push(inputFileData[i]);
        else if (ct.startsWith('audio/')) audioInputs.push(inputFileData[i]);
      }

      // Check if an element or any ancestor is hidden (display:none)
      function __isElementHidden(el) {
        var node = el;
        while (node && node !== document.documentElement) {
          if (node.style && node.style.display === 'none') return true;
          node = node.parentElement;
        }
        return false;
      }

      // Find broken images (no src, empty src, or dead blob URL)
      // Skip images inside hidden containers — on page reload the mini-app may
      // have both a hidden preview img and a visible result img; we must not
      // waste the cached input file on the hidden one.
      var allImgs = document.querySelectorAll('img');
      var brokenImgs = [];
      for (var i = 0; i < allImgs.length; i++) {
        var img = allImgs[i];
        if (__isElementHidden(img)) continue;
        if (!img.src || (img.complete && img.naturalWidth === 0 && img.getAttribute('src'))) {
          brokenImgs.push(img);
        }
      }

      // Fix broken images with cached input images (matched by order)
      var fixed = 0;
      for (var i = 0; i < Math.min(brokenImgs.length, imageInputs.length); i++) {
        var blob = new Blob([new Uint8Array(imageInputs[i].data)], { type: imageInputs[i].contentType });
        brokenImgs[i].src = URL.createObjectURL(blob);
        fixed++;
      }

      // Find and fix broken videos
      var allVideos = document.querySelectorAll('video');
      for (var vi = 0, vIdx = 0; vi < allVideos.length && vIdx < videoInputs.length; vi++) {
        var video = allVideos[vi];
        if (!video.src || video.error) {
          var vblob = new Blob([new Uint8Array(videoInputs[vIdx].data)], { type: videoInputs[vIdx].contentType });
          video.src = URL.createObjectURL(vblob);
          fixed++;
          vIdx++;
        }
      }

      // Find and fix broken audio
      var allAudios = document.querySelectorAll('audio');
      for (var ai = 0, aIdx = 0; ai < allAudios.length && aIdx < audioInputs.length; ai++) {
        var audio = allAudios[ai];
        if (!audio.src || audio.error) {
          var ablob = new Blob([new Uint8Array(audioInputs[aIdx].data)], { type: audioInputs[aIdx].contentType });
          audio.src = URL.createObjectURL(ablob);
          fixed++;
          aIdx++;
        }
      }

      if (fixed > 0) {
        console.log('[Llambada] Restored ' + fixed + ' broken media element(s) after tab recovery');
      }
    } catch(e) {
      console.warn('[Llambada] Media restoration failed:', e);
    }
  }

  function __trackAsync(promise) {
    __pendingRequests++;
    window.parent.postMessage({ type: 'llambada:pendingRequests', count: __pendingRequests }, '*');
    var dec = function() {
      __pendingRequests = Math.max(0, __pendingRequests - 1);
      window.parent.postMessage({ type: 'llambada:pendingRequests', count: __pendingRequests }, '*');
    };
    promise.then(dec, dec);
    return promise;
  }
  
  async function getAuthToken() {
    // Return cached token if available
    if (__authToken) return __authToken;
    
    // If already requesting, wait for that request
    if (__authTokenPromise) return __authTokenPromise;
    
    // Request token from parent
    __authTokenPromise = new Promise((resolve) => {
      __authTokenResolve = resolve;
      window.parent.postMessage({ type: 'llambada:getAuthToken' }, '*');
      // Timeout after 5 seconds
      setTimeout(() => {
        if (__authTokenResolve) {
          __authTokenResolve(null);
          __authTokenResolve = null;
        }
      }, 5000);
    });
    
    __authToken = await __authTokenPromise;
    __authTokenPromise = null;
    return __authToken;
  }

  async function getTurnstileToken() {
    if (__turnstileTokenPromise) return __turnstileTokenPromise;

    __turnstileTokenPromise = new Promise((resolve) => {
      __turnstileTokenResolve = resolve;
      window.parent.postMessage({ type: 'llambada:getTurnstileToken' }, '*');
      setTimeout(() => {
        if (__turnstileTokenResolve) {
          __turnstileTokenResolve(null);
          __turnstileTokenResolve = null;
        }
      }, 30000);
    });

    const token = await __turnstileTokenPromise;
    __turnstileTokenPromise = null;
    return token;
  }
  
  // Helper for API calls
  async function callDataApi(action, params = {}) {
    const token = await getAuthToken();
    if (!token) {
      throw new Error('Not authenticated. Please sign in to use data storage.');
    }
    
    const url = new URL(__supabaseUrl + '/functions/v1/app-data');
    url.searchParams.set('projectId', __projectId);
    
    const response = await fetch(url.toString(), {
      method: 'POST',
      headers: {
        'Authorization': 'Bearer ' + token,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ action, ...params })
    });
    
    const result = await response.json();
    
    if (result.error) {
      const err = new Error(result.message || result.error);
      err.code = result.error;
      err.details = result;
      throw err;
    }
    
    return result;
  }

  var __toastHostReady = false;
  var __toastRequestSeq = 0;
  var __pendingToastAcks = new Map();
  var __queuedToastRequests = [];

  function normalizeToastTone(tone) {
    if (tone === 'success' || tone === 'error' || tone === 'warning') return tone;
    return '';
  }

  function normalizeToastPayload(message, tone, duration) {
    if (Date.now() < __restoreToastSuppressUntil) return null;

    var text = '';
    if (typeof message === 'string') {
      text = message.trim();
    } else if (message !== undefined && message !== null) {
      text = String(message).trim();
    }
    if (!text) return null;

    var timeout = typeof duration === 'number' && isFinite(duration) ? duration : 3000;
    if (timeout < 1000) timeout = 1000;
    if (timeout > 15000) timeout = 15000;

    return {
      message: text,
      tone: normalizeToastTone(tone),
      duration: timeout,
    };
  }

  function flushQueuedPlatformToasts() {
    if (!__toastHostReady) return;
    while (__queuedToastRequests.length > 0) {
      window.parent.postMessage(__queuedToastRequests.shift(), '*');
    }
  }

  function emitPlatformToast(message, tone, duration) {
    var normalized = normalizeToastPayload(message, tone, duration);
    if (!normalized) return Promise.resolve(false);

    var requestId = 'toast-' + Date.now() + '-' + (++__toastRequestSeq);
    var payload = {
      type: 'llambada:showToast',
      requestId: requestId,
      message: normalized.message,
      tone: normalized.tone,
      duration: normalized.duration,
    };

    return new Promise(function(resolve) {
      var timeoutId = window.setTimeout(function() {
        if (!__pendingToastAcks.has(requestId)) return;
        __pendingToastAcks.delete(requestId);
        resolve(false);
      }, 5000);

      __pendingToastAcks.set(requestId, { resolve: resolve, timeoutId: timeoutId });

      if (__toastHostReady) {
        window.parent.postMessage(payload, '*');
      } else {
        __queuedToastRequests.push(payload);
      }
    });
  }

  window.llambada = {
    // Current theme ('light' or 'dark') - updated by platform on theme changes
    theme: document.documentElement.classList.contains('dark') ? 'dark' : 'light',
    
    // The canonical URL of this mini-app (for constructing share links)
    // Use this instead of location.origin/pathname since the iframe runs in a blob URL context
    appUrl: __appUrl,
    
    // URL parameters from the parent page's URL
    // Use this instead of location.search since the iframe runs in a blob URL context
    urlParams: __urlParams,
    
    // User context - available synchronously on page load
    // Contains: id, email, displayName, tier, isAuthenticated, maxPayloadBytes, embeddingBatchLimit
    user: {
      id: __userContext.id,
      email: __userContext.email,
      displayName: __userContext.displayName,
      tier: __userContext.tier,
      isAuthenticated: !!__userContext.id,
      locale: __userContext.locale || 'en',
      creditsAvailable: __userContext.creditsAvailable || 0,
      creditsLoaded: !!__userContext.creditsLoaded,
      creditCosts: __userContext.creditCosts || {},
      maxPayloadBytes: Number(__userContext.maxPayloadBytes),
      embeddingBatchLimit: __userContext.embeddingBatchLimit || 50
    },

    // Family fork parameters (when present)
    // Read-only metadata for shared frontend logic.
    // Backward compat: also expose inputFormat/outputFormat at top level of family
    // for existing mini-app frontends that read window.Llambada.family.inputFormat.
    family: __familyConfig ? Object.assign({}, __familyConfig, {
      inputFormat: __familyConfig.params?.input_format || undefined,
      outputFormat: __familyConfig.params?.output_format || undefined,
    }) : null,

    toast: function(message, tone, duration) {
      return emitPlatformToast(message, tone, duration);
    },

    // Build a direct URL to a static asset bundled under the mini-app's
    // frontend/ folder. Use this for <img src>, <audio src>, <video src>,
    // CSS url(), etc. so the browser's native resource loader fetches them
    // in parallel — no JS fetch() required.
    //
    // Example:
    //   const img = document.createElement('img');
    //   img.src = llambada.fileUrl('images/drake.jpg');
    //
    // The path is relative to the mini-app's frontend/ folder; do NOT
    // include the "frontend/" prefix yourself. Only files under frontend/
    // are accessible — backend/ files cannot be served this way.
    fileUrl: function(path) {
      if (typeof path !== 'string' || !path) return '';
      // Strip any leading ./ or / and an optional leading 'frontend/'
      var clean = path.replace(/^\\.?\\//, '').replace(/^frontend\\//, '');
      var base = window.__LLAMBADA_SUPABASE_URL || '';
      var pid = __projectId || '';
      var url = base + '/functions/v1/project-code?projectId=' + encodeURIComponent(pid) +
        '&raw=1&filename=frontend/' + clean.split('/').map(encodeURIComponent).join('/');
      // In private/dev mode (unpublished projects), the raw endpoint requires
      // auth. Native resource loaders can't send headers, so we pass the
      // cached auth token as a query param.
      if (!__publicMode && __authToken) {
        url += '&token=' + encodeURIComponent(__authToken);
      }
      return url;
    },

    showToast: function(message, tone, duration) {
      return window.llambada.toast(message, tone, duration);
    },

    showToastAt: function(_element, message, tone, duration) {
      return window.llambada.toast(message, tone, duration);
    },
    
    submit: async function(formData) {
      if (isBackendBuilding) {
        window.dispatchEvent(new CustomEvent('jobError', { 
          detail: { code: 'BACKEND_BUILDING', message: 'Backend is currently rebuilding. Please wait 1-2 minutes and try again.' } 
        }));
        return;
      }
      const entries = await serializeFormData(formData);
      window.parent.postMessage({
        type: 'llambada:submit',
        entries: entries
      }, '*');
    },
    
    isBuilding: function() {
      return isBackendBuilding;
    },

    // Read-only count of in-flight platform API requests.
    // Mini-apps can use this to show "saving..." indicators or disable close buttons.
    get pendingRequests() { return __pendingRequests; },
    
    // =========================================================================
    // Persistent Data Storage API
    // =========================================================================
    
    /**
     * Get data from storage.
     * @param {string} collection - Collection name (e.g., 'contacts', 'settings')
     * @param {string} [recordId] - Optional record ID. If omitted, returns all records in collection.
     * @returns {Promise<object|array>} Single record data or array of records
     */
    getData: function(collection, recordId) {
      if (!collection) throw new Error('Collection name is required');

      return __trackAsync((async function() {
        const result = await callDataApi('get', { collection, recordId });

        if (recordId) {
          return result.record?.data || null;
        }
        return (result.records || []).map(r => ({ _id: r.record_id, _updatedAt: r.updated_at, ...r.data }));
      })());
    },
    
    /**
     * Save data to storage.
     * @param {string} collection - Collection name
     * @param {string} recordId - Unique record ID within the collection
     * @param {object} data - Data to store (must be JSON-serializable)
     * @returns {Promise<object>} Result with storage usage info
     */
    setData: function(collection, recordId, data) {
      if (!collection) throw new Error('Collection name is required');
      if (!recordId) throw new Error('Record ID is required');
      if (data === undefined) throw new Error('Data is required');

      return __trackAsync(callDataApi('set', { collection, recordId, data }));
    },
    
    /**
     * Delete a record from storage.
     * @param {string} collection - Collection name
     * @param {string} recordId - Record ID to delete
     * @returns {Promise<object>} Result
     */
    deleteData: function(collection, recordId) {
      if (!collection) throw new Error('Collection name is required');
      if (!recordId) throw new Error('Record ID is required');

      return __trackAsync(callDataApi('delete', { collection, recordId }));
    },
    
    /**
     * List records in a collection (IDs only) or list all collections.
     * @param {string} [collection] - Collection name. If omitted, lists all collections.
     * @returns {Promise<object>} List of record IDs or collection names
     */
    listData: function(collection) {
      return __trackAsync(callDataApi('list', { collection }));
    },
    
    /**
     * Delete all records in a collection.
     * @param {string} collection - Collection name
     * @returns {Promise<object>} Result with count of deleted records
     */
    deleteCollection: function(collection) {
      if (!collection) throw new Error('Collection name is required');

      return __trackAsync(callDataApi('delete_collection', { collection }));
    },
    
    /**
     * Delete all user data for this app.
     * @returns {Promise<object>} Result with count of deleted records
     */
    clearAllData: function() {
      return __trackAsync(callDataApi('clear_all', {}));
    },
    
    /**
     * Get storage usage information.
     * @returns {Promise<object>} Storage info including used bytes, limit, and tier
     */
    storageInfo: function() {
      return __trackAsync(callDataApi('storage_info', {}));
    },
    
    /**
     * Check if user is authenticated (can use data storage).
     * @returns {Promise<boolean>}
     */
    isAuthenticated: async function() {
      const token = await getAuthToken();
      return !!token;
    },
    
    // =========================================================================
    // Vector Storage API (Similarity Search)
    // =========================================================================
    
    /**
     * Store a vector embedding.
     * @param {string} collection - Collection name (e.g., 'documents', 'products')
     * @param {string} recordId - Unique record ID within the collection
     * @param {number[]} embedding - Vector embedding (any dimension)
     * @param {object} [options] - Optional settings
     * @param {string} [options.content] - Original text content
     * @param {object} [options.metadata] - Filterable metadata
     * @returns {Promise<object>} Result
     */
    setVector: function(collection, recordId, embedding, options = {}) {
      if (!collection) throw new Error('Collection name is required');
      if (!recordId) throw new Error('Record ID is required');
      if (!embedding || !Array.isArray(embedding)) throw new Error('Embedding array is required');

      return __trackAsync((async function() {
        const token = await getAuthToken();
        if (!token) return _localVectorUpsert(collection, recordId, embedding, options.content, options.metadata);

        return await callVectorApi('upsert', {
          collection,
          recordId,
          embedding,
          content: options.content,
          metadata: options.metadata
        });
      })());
    },
    
    /**
     * Store multiple vector embeddings in one batch request.
     * @param {Array<{collection: string, recordId: string, embedding: number[], content?: string, metadata?: object}>} vectors
     * @returns {Promise<object>} Result with count
     */
    setVectors: function(vectors) {
      if (!vectors || !Array.isArray(vectors) || vectors.length === 0) throw new Error('vectors array is required and must not be empty');
      for (var i = 0; i < vectors.length; i++) {
        var v = vectors[i];
        if (!v.collection) throw new Error('Vector at index ' + i + ': collection is required');
        if (!v.recordId) throw new Error('Vector at index ' + i + ': recordId is required');
        if (!v.embedding || !Array.isArray(v.embedding)) throw new Error('Vector at index ' + i + ': embedding array is required');
      }

      return __trackAsync((async function() {
        var token = await getAuthToken();
        if (!token) {
          // Local fallback: loop through each vector
          for (var j = 0; j < vectors.length; j++) {
            _localVectorUpsert(vectors[j].collection, vectors[j].recordId, vectors[j].embedding, vectors[j].content, vectors[j].metadata);
          }
          return { success: true, count: vectors.length };
        }

        return await callVectorApi('batch_upsert', { vectors: vectors });
      })());
    },

    /**
     * Search for similar vectors.
     * @param {string} collection - Collection name
     * @param {number[]} embedding - Query vector embedding
     * @param {object} [options] - Search options
     * @param {number} [options.topK=10] - Number of results (max 100)
     * @param {object} [options.filter] - Metadata filter
     * @returns {Promise<array>} Array of {recordId, content, metadata, similarity}
     */
    searchVectors: function(collection, embedding, options = {}) {
      if (!collection) throw new Error('Collection name is required');
      if (!embedding || !Array.isArray(embedding)) throw new Error('Embedding array is required');

      return __trackAsync((async function() {
        const token = await getAuthToken();
        if (!token) return _localVectorSearch(collection, embedding, options.topK || 10, options.filter);

        const result = await callVectorApi('search', {
          collection,
          embedding,
          topK: options.topK || 10,
          filter: options.filter
        });

        // Transform snake_case from database to camelCase for API consistency
        return (result.results || []).map(r => ({
          recordId: r.record_id,
          content: r.content,
          metadata: r.metadata,
          similarity: r.similarity
        }));
      })());
    },
    
    /**
     * Get a single vector by ID.
     * @param {string} collection - Collection name
     * @param {string} recordId - Record ID
     * @returns {Promise<object|null>} Vector record or null
     */
    getVector: function(collection, recordId) {
      if (!collection) throw new Error('Collection name is required');
      if (!recordId) throw new Error('Record ID is required');

      return __trackAsync((async function() {
        const token = await getAuthToken();
        if (!token) return _localVectorGet(collection, recordId);

        const url = new URL(__supabaseUrl + '/functions/v1/app-vectors');
        url.searchParams.set('projectId', __projectId);
        url.searchParams.set('collection', collection);
        url.searchParams.set('recordId', recordId);

        const response = await fetch(url.toString(), {
          headers: { 'Authorization': 'Bearer ' + token }
        });

        const result = await response.json();
        if (result.error) throw new Error(result.error);

        // Transform snake_case from database to camelCase for API consistency
        if (!result.record) return null;
        return {
          recordId: result.record.record_id,
          content: result.record.content,
          metadata: result.record.metadata,
          createdAt: result.record.created_at,
          updatedAt: result.record.updated_at
        };
      })());
    },
    
    /**
     * Delete a vector.
     * @param {string} collection - Collection name
     * @param {string} recordId - Record ID
     * @returns {Promise<object>} Result
     */
    deleteVector: function(collection, recordId) {
      if (!collection) throw new Error('Collection name is required');
      if (!recordId) throw new Error('Record ID is required');

      return __trackAsync((async function() {
        const token = await getAuthToken();
        if (!token) return _localVectorDelete(collection, recordId);

        return await callVectorApi('delete', { collection, recordId });
      })());
    },
    
    /**
     * List vectors in a collection or list all vector collections.
     * @param {string} [collection] - Collection name (optional)
     * @returns {Promise<object>} List of record IDs or collection names
     */
    listVectors: function(collection) {
      return __trackAsync((async function() {
        const token = await getAuthToken();
        if (!token) return _localVectorList(collection);

        return await callVectorApi('list', { collection });
      })());
    },
    
    /**
     * Delete all vectors in a collection.
     * @param {string} collection - Collection name
     * @returns {Promise<object>} Result with deletedCount
     */
    deleteVectorCollection: function(collection) {
      if (!collection) throw new Error('Collection name is required');

      return __trackAsync((async function() {
        const token = await getAuthToken();
        if (!token) return _localVectorDeleteCollection(collection);

        return await callVectorApi('delete_collection', { collection });
      })());
    },
    
    /**
     * Get vector storage statistics.
     * @returns {Promise<object>} Stats including count and limit
     */
    vectorStats: function() {
      return __trackAsync((async function() {
        const token = await getAuthToken();
        if (!token) return _localVectorStats();

        return await callVectorApi('stats', {});
      })());
    },
    
    /**
     * Generate embeddings for text (platform service).
     * @param {string|string[]} texts - Text or array of texts to embed
     * @param {object} [options] - Options
     * @param {string} [options.model='text-embedding-3-small'] - Embedding model
     * @returns {Promise<number[][]>} Array of embeddings
     */
    generateEmbeddings: function(texts, options = {}) {
      const textsArray = Array.isArray(texts) ? texts : [texts];
      if (textsArray.length === 0) throw new Error('At least one text is required');

      return __trackAsync((async function() {
        const token = await getAuthToken();
        const headers = {
          'Content-Type': 'application/json'
        };
        if (token) {
          headers['Authorization'] = 'Bearer ' + token;
        }

        const turnstileToken = token ? null : await getTurnstileToken();

        const url = new URL(__supabaseUrl + '/functions/v1/generate-embeddings');
        url.searchParams.set('projectId', __projectId);

        const response = await fetch(url.toString(), {
          method: 'POST',
          headers,
          body: JSON.stringify({
            texts: textsArray,
            model: options.model || 'text-embedding-3-small',
            projectId: __projectId,
            turnstileToken: turnstileToken || undefined
          })
        });

        const result = await response.json();
        if (result.error) throw new Error(result.error);

        return result.embeddings;
      })());
    },
    
    // =========================================================================
    // LLM Completion API (supports multimodal: text, images, audio, video, PDFs, ZIPs)
    // =========================================================================

    /**
     * Convert a File or Blob to a base64 data URL string.
     * Useful for sending images, audio, video, or PDFs to generateCompletion.
     * @param {File|Blob} file - The file to convert
     * @returns {Promise<string>} Base64 data URL (e.g. "data:image/png;base64,...")
     */
    fileToBase64: function(file) {
      return new Promise(function(resolve, reject) {
        var reader = new FileReader();
        reader.onload = function() { resolve(reader.result); };
        reader.onerror = function() { reject(new Error('Failed to read file')); };
        reader.readAsDataURL(file);
      });
    },

    /**
     * Convert a File or Blob to raw base64 string (no data URL prefix).
     * Useful for audio input_audio format which requires raw base64.
     * @param {File|Blob} file - The file to convert
     * @returns {Promise<string>} Raw base64 string
     */
    fileToRawBase64: function(file) {
      return new Promise(function(resolve, reject) {
        var reader = new FileReader();
        reader.onload = function() {
          var dataUrl = reader.result;
          var base64 = dataUrl.split(',')[1] || dataUrl;
          resolve(base64);
        };
        reader.onerror = function() { reject(new Error('Failed to read file')); };
        reader.readAsDataURL(file);
      });
    },

    /**
     * Generate LLM completion (supports multimodal inputs).
     * Rate limited: 250 requests per hour per user.
     * Token limits: 10,000 tokens max text input, 10,000 tokens max output.
     * File limits: 20 MB max total payload, 10 files max per request.
     *
     * @param {Array<{role: string, content: string|Array}>} messages - Conversation messages.
     *   content can be a plain string (text only) or an array of content parts:
     *     {type: "text", text: "..."}
     *     {type: "image_url", image_url: {url: "data:image/png;base64,..."}}
     *     {type: "input_audio", input_audio: {data: "raw_base64", format: "wav"}}
     *     {type: "video_url", video_url: {url: "data:video/mp4;base64,..."}}
     *     {type: "file", file: {filename: "doc.pdf", file_data: "data:application/pdf;base64,..."}}
     *     {type: "zip_archive", zip_archive: {data: "raw_base64", filename: "archive.zip"}}
     * @param {object} [options] - Options
     * @param {string} [options.model='fast'] - LLM model: 'fast' (default), 'smart', 'multimodal', 'multilingual', 'imagegen'. imagegen accepts text prompts and optional reference images (image_url parts) for style-consistent generation.
     * @param {boolean} [options.trackPendingRequest=true] - Whether this call should trigger the platform leave-page guard while in-flight.
     * @returns {Promise<{content: string, model: string, usage: {input_tokens: number, output_tokens: number}}>}
     */
	    generateCompletion: function(messages, options = {}) {
      if (!Array.isArray(messages) || messages.length === 0) {
        throw new Error('Messages array is required');
      }

      var requestPromise = (async function() {
        var token = await getAuthToken();
        var headers = {
          'Content-Type': 'application/json'
        };
        if (token) {
          headers['Authorization'] = 'Bearer ' + token;
        }

        var url = new URL(__supabaseUrl + '/functions/v1/generate-completion');

        var response = await fetch(url.toString(), {
          method: 'POST',
          headers,
          body: JSON.stringify({
            messages: messages,
            model: options.model || 'fast',
            projectId: __projectId,
            stream: true
          })
        });

        // Handle SSE streaming responses (text models) by buffering
        // the stream and returning the final assembled result.
        var contentType = response.headers.get('content-type') || '';
        if (contentType.includes('text/event-stream')) {
          var reader = response.body.getReader();
          var decoder = new TextDecoder();
          var finalResult = null;
          var buf = '';

          while (true) {
            var chunk = await reader.read();
            if (chunk.done) break;
            buf += decoder.decode(chunk.value, { stream: true });
            var parts = buf.split('\\n\\n');
            buf = parts.pop() || '';
            for (var i = 0; i < parts.length; i++) {
              var part = parts[i].trim();
              if (!part.startsWith('data: ')) continue;
              try {
                var evt = JSON.parse(part.slice(6));
                if (evt.error) {
                  var err3 = new Error(evt.error);
                  err3.code = evt.code || 'LLM_ERROR';
                  throw err3;
                }
                if (evt.done) finalResult = evt;
              } catch (e) {
                if (e.code) throw e;
              }
            }
          }

          if (!finalResult) {
            throw new Error('Stream ended without a result');
          }
          return { content: finalResult.content, model: finalResult.model, usage: finalResult.usage };
        }

        // Non-streaming response (imagegen, errors)
        var result = await response.json();

        if (result.error) {
          var err2 = new Error(result.error);
          err2.code = result.code || 'LLM_ERROR';
          if (result.retryAfter) err2.retryAfter = result.retryAfter;
          throw err2;
        }

        return result;
      })();

	      var trackPendingRequest = options.trackPendingRequest !== false;
	      return trackPendingRequest ? __trackAsync(requestPromise) : requestPromise;
	    },

	    /**
	     * Generate a batch of independent text-only LLM completions in one API call.
	     * Supported models: 'fast', 'multilingual'.
	     * Each batch item must be <= 200 input tokens. The total batch input must
	     * fit within the same 10,000-token limit as a single call.
	     *
	     * @param {Array<{messages: Array<{role: string, content: string|Array}>}>} batch - Independent text-only tasks.
	     * @param {object} [options] - Options
	     * @param {string} [options.model='fast'] - Text-only model: 'fast' or 'multilingual'
	     * @param {boolean} [options.trackPendingRequest=true] - Whether this call should trigger the platform leave-page guard while in-flight.
	     * @returns {Promise<{batch: string[], model: string, usage: {input_tokens: number, output_tokens: number}, batch_mode: true, truncated_by_platform: boolean}>}
	     */
	    generateCompletionBatch: function(batch, options = {}) {
	      if (!Array.isArray(batch) || batch.length === 0) {
	        throw new Error('Batch array is required');
	      }

	      var requestPromise = (async function() {
	        var token = await getAuthToken();
	        var headers = {
	          'Content-Type': 'application/json'
	        };
	        if (token) {
	          headers['Authorization'] = 'Bearer ' + token;
	        }

	        var url = new URL(__supabaseUrl + '/functions/v1/generate-completion');
	        var response = await fetch(url.toString(), {
	          method: 'POST',
	          headers,
	          body: JSON.stringify({
	            batch: batch,
	            model: options.model || 'fast',
	            projectId: __projectId
	          })
	        });

	        var result = await response.json();
	        if (result.error) {
	          var err = new Error(result.error);
	          err.code = result.code || 'LLM_ERROR';
	          if (result.retryAfter) err.retryAfter = result.retryAfter;
	          if (typeof result.batchIndex === 'number') err.batchIndex = result.batchIndex;
	          throw err;
	        }

	        if (!Array.isArray(result.batch)) {
	          throw new Error('Batch response missing batch array');
	        }

	        return result;
	      })();

	      var trackPendingRequest = options.trackPendingRequest !== false;
	      return trackPendingRequest ? __trackAsync(requestPromise) : requestPromise;
	    },

	    // Streaming variant of generateCompletion — calls onDelta(text) for each
    // chunk and resolves with the final {content, model, usage} once done.
    generateCompletionStream: function(messages, options = {}, onDelta) {
      if (typeof options === 'function') { onDelta = options; options = {}; }
      if (!onDelta || typeof onDelta !== 'function') {
        throw new Error('onDelta callback is required for generateCompletionStream');
      }
      if (!Array.isArray(messages) || messages.length === 0) {
        throw new Error('Messages array is required');
      }

      var requestPromise = (async function() {
        var token = await getAuthToken();
        var headers = { 'Content-Type': 'application/json' };
        if (token) { headers['Authorization'] = 'Bearer ' + token; }

        var url = new URL(__supabaseUrl + '/functions/v1/generate-completion');

        var response = await fetch(url.toString(), {
          method: 'POST',
          headers,
          body: JSON.stringify({
            messages: messages,
            model: options.model || 'fast',
            projectId: __projectId,
            stream: true
          })
        });

        var contentType = response.headers.get('content-type') || '';
        if (contentType.includes('text/event-stream')) {
          var reader = response.body.getReader();
          var decoder = new TextDecoder();
          var finalResult = null;
          var buf = '';

          while (true) {
            var chunk = await reader.read();
            if (chunk.done) break;
            buf += decoder.decode(chunk.value, { stream: true });
            var parts = buf.split('\\n\\n');
            buf = parts.pop() || '';
            for (var i = 0; i < parts.length; i++) {
              var part = parts[i].trim();
              if (!part.startsWith('data: ')) continue;
              try {
                var evt = JSON.parse(part.slice(6));
                if (evt.error) {
                  var err3 = new Error(evt.error);
                  err3.code = evt.code || 'LLM_ERROR';
                  throw err3;
                }
                if (evt.delta) {
                  onDelta(evt.delta);
                }
                if (evt.done) finalResult = evt;
              } catch (e) {
                if (e.code) throw e;
              }
            }
          }

          if (!finalResult) {
            throw new Error('Stream ended without a result');
          }
          return { content: finalResult.content, model: finalResult.model, usage: finalResult.usage };
        }

        // Non-streaming fallback (imagegen, errors)
        var result = await response.json();
        if (result.error) {
          var err2 = new Error(result.error);
          err2.code = result.code || 'LLM_ERROR';
          if (result.retryAfter) err2.retryAfter = result.retryAfter;
          throw err2;
        }
        // Call onDelta with the full content for non-streaming responses
        if (result.content) onDelta(result.content);
        return result;
      })();

      var trackPendingRequest = options.trackPendingRequest !== false;
      return trackPendingRequest ? __trackAsync(requestPromise) : requestPromise;
    },

    // =========================================================================
    // File Storage API
    // Pattern: uploadFile(collection, fileId, file, metadata)
    //          getFile(collection, fileId)
    //          deleteFile(collection, fileId)
    // Matches Data API: setData(collection, recordId, data)
    // Matches Vector API: setVector(collection, recordId, embedding, options)
    // =========================================================================
    
    /**
     * Upload a file.
     * @param {string} collection - Collection name (e.g., 'attachments', 'images')
     * @param {string} fileId - Unique file identifier
     * @param {File|Blob|ArrayBuffer|Uint8Array} file - File data to upload
     * @param {object} [metadata] - Optional metadata
     * @param {string} [metadata.name] - Display filename
     * @param {string} [metadata.type] - MIME type (e.g., 'image/png')
     * @returns {Promise<{fileId: string, filename: string, size: number, collection: string}>}
     */
    uploadFile: function(collection, fileId, file, metadata = {}) {
      if (!collection) throw new Error('Collection is required');
      if (!fileId) throw new Error('File ID is required');
      if (!file) throw new Error('File data is required');

      return __trackAsync((async function() {
        const token = await getAuthToken();
        if (!token) {
          const err = new Error('Not authenticated. Please sign in to upload files.');
          err.code = 'AUTH_REQUIRED';
          throw err;
        }

        // Convert ArrayBuffer/Uint8Array to Blob if needed
        let fileBlob = file;
        let contentType = metadata.type || 'application/octet-stream';
        let filename = metadata.name || fileId;

        if (file instanceof ArrayBuffer || file instanceof Uint8Array) {
          const data = file instanceof ArrayBuffer ? new Uint8Array(file) : file;
          fileBlob = new Blob([data], { type: contentType });
        } else if (file instanceof Blob || file instanceof File) {
          contentType = file.type || contentType;
          if (file instanceof File) {
            filename = metadata.name || file.name || fileId;
          }
          fileBlob = file;
        }

        const url = new URL(__supabaseUrl + '/functions/v1/app-files');
        url.searchParams.set('projectId', __projectId);

        const formData = new FormData();
        formData.append('action', 'upload');
        formData.append('file', fileBlob, filename);
        formData.append('fileId', fileId);
        formData.append('filename', filename);
        formData.append('collection', collection);
        formData.append('contentType', contentType);
        if (metadata.customMetadata) {
          formData.append('metadata', JSON.stringify({ customMetadata: metadata.customMetadata }));
        }

        const response = await fetch(url.toString(), {
          method: 'POST',
          headers: {
            'Authorization': 'Bearer ' + token,
          },
          body: formData
        });

        const result = await response.json();

        if (result.error) {
          const err = new Error(result.message || result.error);
          err.code = result.error;
          err.details = result;
          throw err;
        }

        return result;
      })());
    },
    
    /**
     * Get (download) a file.
     * @param {string} collection - Collection name
     * @param {string} fileId - File ID to download
     * @returns {Promise<Blob>} File contents as Blob
     */
    getFile: function(collection, fileId) {
      if (!collection) throw new Error('Collection is required');
      if (!fileId) throw new Error('File ID is required');

      return __trackAsync((async function() {
        const token = await getAuthToken();
        if (!token) {
          const err = new Error('Not authenticated. Please sign in to download files.');
          err.code = 'AUTH_REQUIRED';
          throw err;
        }

        const url = new URL(__supabaseUrl + '/functions/v1/app-files');
        url.searchParams.set('projectId', __projectId);

        const response = await fetch(url.toString(), {
          method: 'POST',
          headers: {
            'Authorization': 'Bearer ' + token,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'download',
            fileId,
            collection,
          })
        });

        if (!response.ok) {
          const result = await response.json();
          const err = new Error(result.message || result.error || 'Download failed');
          err.code = result.error || 'DOWNLOAD_ERROR';
          throw err;
        }

        return await response.blob();
      })());
    },
    
    /**
     * Delete a file.
     * @param {string} collection - Collection name
     * @param {string} fileId - File ID to delete
     * @returns {Promise<{success: boolean}>}
     */
    deleteFile: function(collection, fileId) {
      if (!collection) throw new Error('Collection is required');
      if (!fileId) throw new Error('File ID is required');

      return __trackAsync(callFileApi('delete', {
        fileId,
        collection,
      }));
    },

    /**
     * List files in a collection or all files.
     * @param {string} [collection] - Collection name (omit for all collections)
     * @returns {Promise<object>} Files in collection or all collections
     */
    listFiles: function(collection) {
      return __trackAsync(callFileApi('list', {
        collection: collection,
      }));
    },

    /**
     * Get file storage statistics.
     * @returns {Promise<object>} Storage stats including used bytes, limit, tier
     */
    fileStats: function() {
      return __trackAsync(callFileApi('stats', {}));
    },

    // =========================================================================
    // Game Leaderboard API
    // =========================================================================

    /**
     * Submit a score to the global leaderboard.
     * Only keeps the player's personal best — if the new score isn't better, it's ignored.
     * Only works for projects with type 'game'.
     * @param {number} score - The score value (must be an integer)
     * @param {object} [options] - Options
     * @param {string} [options.sortDirection='desc'] - 'desc' (higher=better) or 'asc' (lower=better)
     * @param {string} [options.partnerId] - Partner's user ID for two-player pair scores
     * @param {object} [options.metadata] - Optional metadata (level, time, character, etc.)
     * @returns {Promise<{success: boolean, isNewBest: boolean, previousBest: number|null, currentRank: number}>}
     */
    submitScore: function(score, options) {
      if (typeof score !== 'number' || !Number.isInteger(score)) {
        throw new Error('Score must be an integer');
      }
      options = options || {};
      var mode = options.partnerId ? 'pair' : 'single';
      return __trackAsync(callScoreApi('submit', {
        score: score,
        mode: mode,
        sortDirection: options.sortDirection || 'desc',
        partnerId: options.partnerId || null,
        metadata: options.metadata || {}
      }));
    },

    /**
     * Get the top scores for this game's leaderboard.
     * Works without authentication — leaderboards are public.
     * @param {object} [options] - Options
     * @param {string} [options.mode='single'] - 'single' for individual or 'pair' for two-player teams
     * @param {number} [options.limit=10] - Number of scores to return (max 100)
     * @returns {Promise<{scores: Array<{rank, score, playerDisplayName, playerId, partnerDisplayName, partnerId, metadata, updatedAt}>, totalPlayers: number}>}
     */
    getTopScores: function(options) {
      options = options || {};
      return __trackAsync(callScoreApi('get_top', {
        mode: options.mode || 'single',
        limit: Math.min(options.limit || 10, 100)
      }));
    },

    /**
     * Get the current user's best score and rank for this game.
     * @param {object} [options] - Options
     * @param {string} [options.mode='single'] - 'single' or 'pair'
     * @returns {Promise<{score: number, rank: number, metadata: object, updatedAt: string}|null>}
     */
    getMyScore: function(options) {
      options = options || {};
      return __trackAsync(callScoreApi('get_my_score', {
        mode: options.mode || 'single'
      }));
    },

    // =========================================================================
    // Multiplayer Room API
    // =========================================================================
    
    /**
     * Join a multiplayer room. Creates the room if it doesn't exist.
     * Requires frontend/room.js to exist in the project.
     * @param {string} roomId - Room identifier (players with the same roomId join the same room)
     * @param {object} [params] - Optional parameters passed to room.js onJoin()
     * @returns {Promise<object>} Room object with send(), onMessage(), onPeerJoin(), onPeerLeave(), leave(), playerId, peers
     */
    joinRoom: async function(roomId, params) {
      if (!roomId) throw new Error('Room ID is required');
      
      var token = await getAuthToken();
      if (!token) {
        var err = new Error('Not authenticated. Please sign in to play multiplayer.');
        err.code = 'AUTH_REQUIRED';
        throw err;
      }
      
      // Deploy room.js to Cloudflare and get WebSocket URL
      var deployResult;
      try {
        var deployUrl = new URL(__supabaseUrl + '/functions/v1/deploy-room');
        var deployResponse = await fetch(deployUrl.toString(), {
          method: 'POST',
          headers: {
            'Authorization': 'Bearer ' + token,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ projectId: __projectId, roomId: roomId })
        });
        
        if (!deployResponse.ok) {
          var errorText = '';
          try { errorText = (await deployResponse.json()).error || deployResponse.statusText; } catch (e) { errorText = deployResponse.statusText; }
          var httpErr = new Error('Room deployment failed: ' + errorText);
          httpErr.code = 'ROOM_DEPLOY_ERROR';
          throw httpErr;
        }
        
        deployResult = await deployResponse.json();
      } catch (fetchErr) {
        if (fetchErr.code === 'ROOM_DEPLOY_ERROR') throw fetchErr;
        var netErr = new Error('Could not reach room server: ' + fetchErr.message);
        netErr.code = 'ROOM_NETWORK_ERROR';
        throw netErr;
      }
      
      if (deployResult.error) {
        var deployErr = new Error(deployResult.error);
        deployErr.code = 'ROOM_DEPLOY_ERROR';
        throw deployErr;
      }
      
      var wsUrl = deployResult.wsUrl;
      // In public mode: use the authenticated user ID as playerId (one connection per user).
      // In dev/test mode: generate a random playerId (allows testing multiplayer from one account).
      var playerId;
      if (__publicMode) {
        if (!__userContext.id) {
          var authErr = new Error('Not authenticated. Please sign in to play.');
          authErr.code = 'AUTH_REQUIRED';
          throw authErr;
        }
        playerId = __userContext.id;
      } else {
        playerId = crypto.randomUUID();
      }
      var wsParams = Object.assign({}, params || {}, {
        authenticated: !!__userContext.id,
        displayName: __userContext.displayName,
        userId: __userContext.id
      });
      
      // Connect WebSocket
      var separator = wsUrl.indexOf('?') !== -1 ? '&' : '?';
      var fullWsUrl = wsUrl + separator + 'playerId=' + encodeURIComponent(playerId) + '&params=' + encodeURIComponent(JSON.stringify(wsParams));
      
      return new Promise(function(resolve, reject) {
        var ws;
        try {
          ws = new WebSocket(fullWsUrl);
        } catch (e) {
          reject(new Error('Failed to connect to room: ' + e.message));
          return;
        }
        
        var messageCallbacks = [];
        var peerJoinCallbacks = [];
        var peerLeaveCallbacks = [];
        var peers = [];
        var resolved = false;
        var messageBuffer = [];
        var peerJoinBuffer = [];
        var peerLeaveBuffer = [];
        
        // ── Shadow room for client-side prediction ──
        var shadowGame = null;
        try {
          var shadowCode = deployResult.shadowCode;
          var shadowExportName = deployResult.shadowExportName;
          if (shadowCode && shadowExportName) {
            var shadowWrapped = shadowCode + '\\nreturn { GameClass: ' + shadowExportName + ', config: typeof config !== "undefined" ? config : null };';

            var shadowBroadcast = function(data) {
              for (var i = 0; i < messageCallbacks.length; i++) {
                try { messageCallbacks[i](data); } catch (e) { console.error('[Llambada:Shadow] prediction callback error:', e); }
              }
            };
            var shadowSend = function(targetId, data) {
              if (targetId === playerId) {
                for (var i = 0; i < messageCallbacks.length; i++) {
                  try { messageCallbacks[i](data); } catch (e) { console.error('[Llambada:Shadow] prediction callback error:', e); }
                }
              }
            };
            var shadowGetPlayers = function() { return [playerId].concat(peers); };
            var shadowGetPlayerCount = function() { return 1 + peers.length; };
            var shadowClose = function() {};

            var shadowMod = { exports: {} };
            var shadowResult = new Function(
              'module', 'exports', 'broadcast', 'send', 'getPlayers', 'getPlayerCount', 'close',
              shadowWrapped
            )(shadowMod, shadowMod.exports, shadowBroadcast, shadowSend, shadowGetPlayers, shadowGetPlayerCount, shadowClose);

            if (shadowResult && shadowResult.GameClass) {
              shadowGame = new shadowResult.GameClass();
              shadowGame.broadcast = shadowBroadcast;
              shadowGame.send = shadowSend;
              shadowGame.getPlayers = shadowGetPlayers;
              shadowGame.getPlayerCount = shadowGetPlayerCount;
              shadowGame.close = shadowClose;
            }
          }
        } catch (shadowErr) {
          console.warn('[Llambada:Shadow] Failed to initialize client prediction:', shadowErr);
          shadowGame = null;
        }
        
        var room = {
          playerId: playerId,
          peers: peers,
          
          send: function(data) {
            if (ws.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify(data));
            }
            // Client-side prediction: feed action into shadow room immediately
            if (shadowGame && typeof shadowGame.onMessage === 'function') {
              try {
                shadowGame.onMessage(playerId, data);
              } catch (e) {
                console.error('[Llambada:Shadow] prediction error:', e);
              }
            }
          },
          
          onMessage: function(callback) {
            messageCallbacks.push(callback);
            // Replay any buffered messages
            if (messageBuffer.length > 0) {
              var buffered = messageBuffer.slice();
              messageBuffer.length = 0;
              for (var i = 0; i < buffered.length; i++) {
                try { callback(buffered[i]); } catch (e) { console.error('[Llambada:Room] onMessage replay error:', e); }
              }
            }
          },
          
          onPeerJoin: function(callback) {
            peerJoinCallbacks.push(callback);
            if (peerJoinBuffer.length > 0) {
              var buffered = peerJoinBuffer.slice();
              peerJoinBuffer.length = 0;
              for (var i = 0; i < buffered.length; i++) {
                try { callback(buffered[i]); } catch (e) { console.error('[Llambada:Room] onPeerJoin replay error:', e); }
              }
            }
          },
          
          onPeerLeave: function(callback) {
            peerLeaveCallbacks.push(callback);
            if (peerLeaveBuffer.length > 0) {
              var buffered = peerLeaveBuffer.slice();
              peerLeaveBuffer.length = 0;
              for (var i = 0; i < buffered.length; i++) {
                try { callback(buffered[i]); } catch (e) { console.error('[Llambada:Room] onPeerLeave replay error:', e); }
              }
            }
          },
          
          leave: function() {
            if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
              ws.close(1000, 'Player left');
            }
          }
        };
        
        ws.onopen = function() {
          console.log('[Llambada:Room] WebSocket connected to room:', roomId);
        };
        
        ws.onmessage = function(event) {
          var msg;
          try {
            msg = JSON.parse(event.data);
          } catch (e) {
            console.warn('[Llambada:Room] Failed to parse message:', e);
            return;
          }
          
          switch (msg.type) {
            case 'assigned':
              // Initial assignment from the Cloudflare worker (not from room.js)
              // Contains playerId and peers list - used for internal room state only.
              // The room.js game code sends its own 'assigned' messages via this.send()
              // which arrive as type:'message' and get forwarded to onMessage callbacks.
              room.playerId = msg.playerId || playerId;
              if (msg.peers) {
                room.peers.length = 0;
                for (var i = 0; i < msg.peers.length; i++) {
                  room.peers.push(msg.peers[i]);
                }
              }
              if (!resolved) {
                resolved = true;
                resolve(room);
              }
              // Initialize shadow room with player join
              if (shadowGame && typeof shadowGame.onJoin === 'function') {
                try { shadowGame.onJoin(room.playerId, wsParams); } catch (e) { console.warn('[Llambada:Shadow] onJoin error:', e); }
              }
              break;
              
            case 'state':
              // Broadcast from game's this.broadcast()
              if (messageCallbacks.length === 0) {
                messageBuffer.push(msg.data);
              } else {
                for (var i = 0; i < messageCallbacks.length; i++) {
                  try { messageCallbacks[i](msg.data); } catch (e) { console.error('[Llambada:Room] onMessage error:', e); }
                }
              }
              break;
              
            case 'message':
              // Direct message from game's this.send()
              if (messageCallbacks.length === 0) {
                messageBuffer.push(msg.data);
              } else {
                for (var i = 0; i < messageCallbacks.length; i++) {
                  try { messageCallbacks[i](msg.data); } catch (e) { console.error('[Llambada:Room] onMessage error:', e); }
                }
              }
              break;
              
            case 'peer_join':
              if (msg.playerId) {
                room.peers.push(msg.playerId);
              }
              // Keep shadow room in sync
              if (shadowGame && typeof shadowGame.onJoin === 'function') {
                try { shadowGame.onJoin(msg.playerId, {}); } catch (e) {}
              }
              if (peerJoinCallbacks.length === 0) {
                peerJoinBuffer.push(msg.playerId);
              } else {
                for (var i = 0; i < peerJoinCallbacks.length; i++) {
                  try { peerJoinCallbacks[i](msg.playerId); } catch (e) { console.error('[Llambada:Room] onPeerJoin error:', e); }
                }
              }
              break;
              
            case 'peer_leave':
              var idx = room.peers.indexOf(msg.playerId);
              if (idx !== -1) room.peers.splice(idx, 1);
              // Keep shadow room in sync
              if (shadowGame && typeof shadowGame.onLeave === 'function') {
                try { shadowGame.onLeave(msg.playerId); } catch (e) {}
              }
              if (peerLeaveCallbacks.length === 0) {
                peerLeaveBuffer.push(msg.playerId);
              } else {
                for (var i = 0; i < peerLeaveCallbacks.length; i++) {
                  try { peerLeaveCallbacks[i](msg.playerId); } catch (e) { console.error('[Llambada:Room] onPeerLeave error:', e); }
                }
              }
              break;
              
            default:
              // Unknown message type - forward to message callbacks or buffer
              if (messageCallbacks.length === 0) {
                messageBuffer.push(msg);
              } else {
                for (var i = 0; i < messageCallbacks.length; i++) {
                  try { messageCallbacks[i](msg); } catch (e) { console.error('[Llambada:Room] onMessage error:', e); }
                }
              }
          }
        };
        
        ws.onerror = function(event) {
          console.error('[Llambada:Room] WebSocket error:', event);
          if (!resolved) {
            resolved = true;
            reject(new Error('Failed to connect to room'));
          }
        };
        
        ws.onclose = function(event) {
          console.log('[Llambada:Room] WebSocket closed:', event.code, event.reason);
          if (!resolved) {
            resolved = true;
            reject(new Error('Connection to room closed: ' + (event.reason || 'unknown')));
          }
        };
        
        // Timeout if server never sends 'assigned'
        setTimeout(function() {
          if (!resolved) {
            resolved = true;
            // Still resolve with the room - some games might not send assigned immediately
            resolve(room);
          }
        }, 5000);
      });
    }
  };

  // For family forks, override the iframe's document.title and the first visible <h1>
  // with the interpolated title (e.g. "Markdown to PDF Converter")
  if (__familyConfig && __familyConfig.title) {
    document.title = __familyConfig.title;
    // Also update the first <h1> on the page once the DOM is ready,
    // preserving any leading icon elements (e.g. <i data-lucide="...">)
    document.addEventListener('DOMContentLoaded', function() {
      var h1 = document.querySelector('h1');
      if (h1) {
        // Keep child elements (icons) but replace text nodes
        var children = Array.from(h1.childNodes);
        for (var i = 0; i < children.length; i++) {
          if (children[i].nodeType === 3) { // TEXT_NODE
            h1.removeChild(children[i]);
          }
        }
        h1.appendChild(document.createTextNode(' ' + __familyConfig.title));
      }
    });
  }

  // Legacy mini-app compatibility:
  // mirror #toast visibility to host-level toasts so it stays in the visible viewport
  function setupLegacyToastBridge() {
    var toast = document.getElementById('toast');
    if (!toast || toast.__llambadaBridgeInstalled) return;
    toast.__llambadaBridgeInstalled = true;

    var lastSignature = '';
    var lastTimestamp = 0;
    var observer = new MutationObserver(function() {
      if (!toast.classList.contains('show')) return;

      var message = (toast.textContent || '').trim();
      if (!message) return;

      var tone = '';
      if (toast.classList.contains('toast-error')) tone = 'error';
      else if (toast.classList.contains('toast-success')) tone = 'success';
      else if (toast.classList.contains('toast-warning')) tone = 'warning';

      var signature = tone + '::' + message;
      var now = Date.now();
      if (signature === lastSignature && now - lastTimestamp < 250) return;
      lastSignature = signature;
      lastTimestamp = now;

      emitPlatformToast(message, tone, 3000);
      toast.classList.remove('show');
    });

    observer.observe(toast, { attributes: true, attributeFilter: ['class'] });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupLegacyToastBridge);
  } else {
    setupLegacyToastBridge();
  }
  
  // Helper for file API calls
  async function callFileApi(action, params = {}) {
    const token = await getAuthToken();
    if (!token) {
      throw new Error('Not authenticated. Please sign in to use file storage.');
    }
    
    const url = new URL(__supabaseUrl + '/functions/v1/app-files');
    url.searchParams.set('projectId', __projectId);
    
    const response = await fetch(url.toString(), {
      method: 'POST',
      headers: {
        'Authorization': 'Bearer ' + token,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ action, ...params })
    });
    
    const result = await response.json();
    
    if (result.error) {
      const err = new Error(result.message || result.error);
      err.code = result.error;
      err.details = result;
      throw err;
    }
    
    return result;
  }
  
  // =========================================================================
  // Browser-based vector store fallback (used when user is not authenticated)
  // =========================================================================
  const __localVectorStore = new Map(); // Map<collection, Map<recordId, {embedding, content, metadata, createdAt, updatedAt}>>

  function _localVectorUpsert(collection, recordId, embedding, content, metadata) {
    if (!__localVectorStore.has(collection)) __localVectorStore.set(collection, new Map());
    const now = new Date().toISOString();
    const existing = __localVectorStore.get(collection).get(recordId);
    __localVectorStore.get(collection).set(recordId, {
      embedding, content: content || null, metadata: metadata || {},
      createdAt: existing ? existing.createdAt : now, updatedAt: now
    });
    return { success: true };
  }

  function _cosineSimilarity(a, b) {
    let dot = 0, normA = 0, normB = 0;
    for (let i = 0; i < a.length; i++) {
      dot += a[i] * b[i];
      normA += a[i] * a[i];
      normB += b[i] * b[i];
    }
    normA = Math.sqrt(normA);
    normB = Math.sqrt(normB);
    if (normA === 0 || normB === 0) return 0;
    return dot / (normA * normB);
  }

  function _localVectorSearch(collection, queryEmbedding, topK, filter) {
    const col = __localVectorStore.get(collection);
    if (!col) return [];
    const results = [];
    col.forEach(function(record, recordId) {
      // Apply metadata filter if provided
      if (filter) {
        for (const key in filter) {
          if (!record.metadata || record.metadata[key] !== filter[key]) return;
        }
      }
      results.push({
        recordId: recordId,
        content: record.content,
        metadata: record.metadata,
        similarity: _cosineSimilarity(queryEmbedding, record.embedding)
      });
    });
    results.sort(function(a, b) { return b.similarity - a.similarity; });
    return results.slice(0, topK || 10);
  }

  function _localVectorGet(collection, recordId) {
    const col = __localVectorStore.get(collection);
    if (!col) return null;
    const record = col.get(recordId);
    if (!record) return null;
    return {
      recordId: recordId,
      content: record.content,
      metadata: record.metadata,
      createdAt: record.createdAt,
      updatedAt: record.updatedAt
    };
  }

  function _localVectorDelete(collection, recordId) {
    const col = __localVectorStore.get(collection);
    if (!col) return { success: true, deleted: false };
    const deleted = col.delete(recordId);
    if (col.size === 0) __localVectorStore.delete(collection);
    return { success: true, deleted: deleted };
  }

  function _localVectorDeleteCollection(collection) {
    const col = __localVectorStore.get(collection);
    const count = col ? col.size : 0;
    __localVectorStore.delete(collection);
    return { success: true, deletedCount: count };
  }

  function _localVectorList(collection) {
    if (collection) {
      const col = __localVectorStore.get(collection);
      return { records: col ? Array.from(col.keys()) : [] };
    }
    return { collections: Array.from(__localVectorStore.keys()) };
  }

  function _localVectorStats() {
    let totalCount = 0;
    __localVectorStore.forEach(function(col) { totalCount += col.size; });
    return { tier: 'free', limit: Infinity, appVectorCount: totalCount, totalVectorCount: totalCount, appUsagePercent: 0, local: true };
  }

  // Helper for vector API calls
  async function callVectorApi(action, params = {}) {
    const token = await getAuthToken();
    if (!token) {
      throw new Error('Not authenticated. Please sign in to use vector storage.');
    }
    
    const url = new URL(__supabaseUrl + '/functions/v1/app-vectors');
    url.searchParams.set('projectId', __projectId);
    
    const response = await fetch(url.toString(), {
      method: 'POST',
      headers: {
        'Authorization': 'Bearer ' + token,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ action, ...params })
    });
    
    const result = await response.json();
    
    if (result.error) {
      const err = new Error(result.message || result.error);
      err.code = result.error;
      err.details = result;
      throw err;
    }
    
    return result;
  }

  // Helper for game score/leaderboard API calls
  async function callScoreApi(action, params = {}) {
    const readActions = ['get_top'];
    const token = await getAuthToken();

    if (!token && !readActions.includes(action)) {
      throw new Error('Not authenticated. Please sign in to submit scores.');
    }

    const url = new URL(__supabaseUrl + '/functions/v1/game-scores');
    url.searchParams.set('projectId', __projectId);

    const headers = { 'Content-Type': 'application/json' };
    if (token) {
      headers['Authorization'] = 'Bearer ' + token;
    }

    const response = await fetch(url.toString(), {
      method: 'POST',
      headers,
      body: JSON.stringify({ action, ...params })
    });

    const result = await response.json();

    if (result.error) {
      const err = new Error(result.message || result.error);
      err.code = result.error;
      err.details = result;
      throw err;
    }

    return result;
  }

  // Auto-intercept form submissions
  async function handleFormSubmit(form) {
    if (!(form instanceof HTMLFormElement)) return;
    if (isBackendBuilding) {
      window.dispatchEvent(new CustomEvent('jobError', { 
        detail: { code: 'BACKEND_BUILDING', message: 'Backend is currently rebuilding. Please wait 1-2 minutes and try again.' } 
      }));
      return;
    }
    const formData = new FormData(form);
    await window.llambada.submit(formData);
  }

  // Listen in bubble phase (not capture) so mini-apps can handle submit first.
  // If the mini-app called e.preventDefault() and window.llambada.submit() with
  // custom FormData, we skip our automatic handling to avoid double-submission.
  document.addEventListener('submit', function(e) {
    if (e.defaultPrevented) return;
    e.preventDefault();
    handleFormSubmit(e.target);
  }, false);

  HTMLFormElement.prototype.submit = (function(originalSubmit) {
    return function() {
      handleFormSubmit(this);
    };
  })(HTMLFormElement.prototype.submit);

  // Listen for messages from platform
  window.addEventListener('message', async function(e) {
    const data = e.data;
    if (!data || !data.type) return;
    
    switch (data.type) {
      case 'llambada:authToken':
        // Always cache the latest token (handles both proactive pushes and request responses)
        __authToken = data.token || null;
        if (__authTokenResolve) {
          __authTokenResolve(__authToken);
          __authTokenResolve = null;
        }
        break;
        
      case 'llambada:authClear':
        // User logged out - clear cached token
        __authToken = null;
        break;

      case 'llambada:turnstileToken':
        if (__turnstileTokenResolve) {
          __turnstileTokenResolve(data.token || null);
          __turnstileTokenResolve = null;
        }
        break;

      case 'llambada:ping':
        // Respond to liveness check from host (used to detect dead blob URLs)
        window.parent.postMessage({ type: 'llambada:pong' }, '*');
        break;

      case 'llambada:toastHostReady':
        __toastHostReady = true;
        flushQueuedPlatformToasts();
        break;

      case 'llambada:toastAck': {
        var ack = data.requestId ? __pendingToastAcks.get(data.requestId) : null;
        if (!ack) break;
        window.clearTimeout(ack.timeoutId);
        __pendingToastAcks.delete(data.requestId);
        ack.resolve(true);
        break;
      }
        
      case 'llambada:themeChange':
        // Dynamically update theme without reloading iframe
        if (data.theme === 'dark') {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }
        // Update the theme property
        window.llambada.theme = data.theme;
        // Dispatch event for mini-apps to react
        window.dispatchEvent(new CustomEvent('themeChange', { detail: { theme: data.theme } }));
        break;
        
      case 'llambada:started':
        // Clear output files from previous job runs before starting new job
        // This prevents stale files from causing issues (e.g., old contents.json
        // being found when checking for extraction_result.json)
        if (window.__clearOutputFiles) {
          window.__clearOutputFiles().then(() => {
            window.dispatchEvent(new CustomEvent('jobStarted'));
          }).catch(err => {
            console.warn('[Llambada] Failed to clear output files:', err);
            window.dispatchEvent(new CustomEvent('jobStarted'));
          });
        } else {
          window.dispatchEvent(new CustomEvent('jobStarted'));
        }
        break;

      case 'llambada:syncFile':
        try {
          const fileBytes = data.buffer instanceof ArrayBuffer
            ? new Uint8Array(data.buffer)
            : new Uint8Array(data.data || []);
          await window.writeFile(data.path, fileBytes);
          window.parent.postMessage({ type: 'llambada:syncComplete', path: data.path }, '*');
          window.dispatchEvent(new CustomEvent('jobUpdate', { detail: { path: data.path } }));
        } catch (err) {
          window.parent.postMessage({ type: 'llambada:syncError', path: data.path, error: err.message }, '*');
        }
        break;

      case 'llambada:measureLayout':
        __scheduleLayoutMeasurement();
        break;
        
      case 'llambada:complete':
        if (data.restored) {
          __restoreToastSuppressUntil = Date.now() + 3000;
          // Verify output files are actually available in __fileStore before
          // dispatching jobComplete.  After a long tab freeze the browser may
          // have evicted __fileStore entries, or the IndexedDB cache may have
          // expired — dispatching jobComplete without the backing files just
          // causes "File not found" errors in mini-apps.
          var _expectedFiles = data.outputFiles || [];
          if (_expectedFiles.length > 0) {
            var _allPresent = _expectedFiles.every(function(f) {
              return __fileStore.has(f.filename);
            });
            if (!_allPresent) {
              console.warn('[Llambada] Skipping restored jobComplete — output files not available in file store');
              break;
            }
          } else {
            // No output files listed — cache likely expired.  Check if __fileStore
            // has any output files at all (files not part of the frontend bundle).
            var _frontendFileNames = new Set(Object.keys(__frontendFiles));
            var _hasOutputFiles = false;
            for (var _key of __fileStore.keys()) {
              if (!_frontendFileNames.has(_key)) { _hasOutputFiles = true; break; }
            }
            if (!_hasOutputFiles) {
              console.warn('[Llambada] Skipping restored jobComplete — no output files in file store (cache expired)');
              break;
            }
          }
        }
        window.dispatchEvent(new CustomEvent('jobComplete', {
          detail: {
            restored: !!data.restored,
            outputFiles: data.outputFiles || []
          }
        }));
        // After tab recovery, wait for the mini-app to finish rendering,
        // then scan the DOM for broken media and fix them with cached input files.
        if (data.restored && data.__inputFileData && data.__inputFileData.length > 0) {
          var __savedInputData = data.__inputFileData;
          setTimeout(function() { __restoreBrokenMedia(__savedInputData); }, 200);
        }
        break;
        
      case 'llambada:error':
        if (data.restored) {
          __restoreToastSuppressUntil = Date.now() + 3000;
        }
        window.dispatchEvent(new CustomEvent('jobError', { 
          detail: { 
            code: data.code || 'UNKNOWN', 
            message: data.message || 'An error occurred.',
            details: data.details || {},
            restored: !!data.restored,
          } 
        }));
        break;
        
      case 'llambada:buildStatus':
        isBackendBuilding = data.isBuilding;
        window.dispatchEvent(new CustomEvent('buildStatusChanged', { detail: { isBuilding: data.isBuilding } }));
        break;
        
      case 'llambada:userContextUpdate':
        // Update user context when auth state changes (e.g., after login/logout)
        if (data.user) {
          window.llambada.user = {
            id: data.user.id,
            email: data.user.email,
            displayName: data.user.displayName,
            tier: data.user.tier,
            isAuthenticated: !!data.user.id,
            locale: data.user.locale || 'en',
            creditsAvailable: data.user.creditsAvailable || 0,
            creditsLoaded: !!data.user.creditsLoaded,
            creditCosts: data.user.creditCosts || {},
            maxPayloadBytes: Number(data.user.maxPayloadBytes),
            embeddingBatchLimit: Number(data.user.embeddingBatchLimit) || 50
          };
          console.log('[Llambada] User context updated:', window.llambada.user);
          // Dispatch event for mini-apps to react to auth changes
          window.dispatchEvent(new CustomEvent('userContextChanged', { 
            detail: { user: window.llambada.user } 
          }));
        }
        break;
    }
  });

  var __layoutTrackingStarted = false;
  var __layoutScheduled = false;
  var __lastReportedLayoutHeight = 0;
  var __lastLayoutDebug = null;

  function __elementDepth(el) {
    var depth = 0;
    while (el && el !== document.body) {
      depth += 1;
      el = el.parentElement;
    }
    return depth;
  }

  function __isVisibleElement(el) {
    if (!(el instanceof HTMLElement)) return false;
    var style = window.getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
    var rect = el.getBoundingClientRect();
    return rect.width > 0 || rect.height > 0;
  }

  function __canScrollVertically(el, style) {
    if (!(el instanceof HTMLElement)) return false;
    var overflowY = style.overflowY || '';
    return /(auto|scroll|overlay)/.test(overflowY) && el.scrollHeight > el.clientHeight + 2;
  }

  function __isLegacyShellScroller(el) {
    if (!(el instanceof HTMLElement) || !document.body) return false;
    if (!__isVisibleElement(el)) return false;

    var style = window.getComputedStyle(el);
    if (style.position === 'fixed') return false;
    if (!__canScrollVertically(el, style)) return false;

    var rect = el.getBoundingClientRect();
    var bodyRect = document.body.getBoundingClientRect();
    var depth = __elementDepth(el);
    var minWidth = bodyRect.width > 260 ? Math.max(220, bodyRect.width * 0.55) : bodyRect.width * 0.7;
    var minHeight = Math.max(220, window.innerHeight * 0.45);
    var anchoredNearTop = rect.top <= Math.max(96, window.innerHeight * 0.2);
    var reachesLowerViewport = rect.bottom >= window.innerHeight - Math.max(32, window.innerHeight * 0.12);

    return depth <= 6 && rect.width >= minWidth && rect.height >= minHeight && anchoredNearTop && reachesLowerViewport;
  }

  function __overrideStyle(el, property, value) {
    if (el && el.style) {
      el.style.setProperty(property, value, 'important');
    }
  }

  function __isViewportSizedShell(el, style, rect, viewportHeight) {
    if (!(el instanceof HTMLElement) || !viewportHeight) return false;
    if (style.position === 'fixed' || style.position === 'absolute') return false;

    var startsAtTop = Math.abs(rect.top) <= 4;
    var endsAtViewportBottom = Math.abs(rect.bottom - viewportHeight) <= 4;
    var fillsViewport = Math.abs(rect.height - viewportHeight) <= 4;
    var usesViewportSizing = /vh|dvh|svh|lvh/.test(style.height || '')
      || /vh|dvh|svh|lvh/.test(style.minHeight || '')
      || style.display === 'flex'
      || style.display === 'grid';

    return startsAtTop && endsAtViewportBottom && fillsViewport && usesViewportSizing;
  }

  function __isRootFixedChrome(el, style, rect) {
    if (!(el instanceof HTMLElement) || !document.body) return false;
    if (style.position !== 'fixed') return false;

    var depth = __elementDepth(el);
    var bodyWidth = document.body.getBoundingClientRect().width || window.innerWidth || 0;
    var wideEnough = bodyWidth > 0 && rect.width >= bodyWidth * 0.6;
    var chromeHeight = rect.height >= 24 && rect.height <= 180;
    var bottomAnchored = style.bottom !== 'auto'
      || Math.abs(rect.bottom - (window.innerHeight || 0)) <= 4;

    return depth <= 4 && wideEnough && chromeHeight && bottomAnchored;
  }

  function __neutralizeRootFixedChrome() {
    var info = { checked: 0, matched: 0, neutralized: 0 };
    if (!document.body) return info;
    if ((window.innerWidth || 0) > 900) return info;

    var nodes = Array.prototype.slice.call(document.body.querySelectorAll('*'));
    for (var i = 0; i < nodes.length; i += 1) {
      var el = nodes[i];
      if (!(el instanceof HTMLElement) || !__isVisibleElement(el)) continue;

      var style = window.getComputedStyle(el);
      var rect = el.getBoundingClientRect();
      if (style.position === 'fixed') info.checked += 1;
      if (!__isRootFixedChrome(el, style, rect)) continue;

      info.matched += 1;
      __overrideStyle(el, 'position', 'static');
      __overrideStyle(el, 'inset', 'auto');
      __overrideStyle(el, 'left', 'auto');
      __overrideStyle(el, 'right', 'auto');
      __overrideStyle(el, 'top', 'auto');
      __overrideStyle(el, 'bottom', 'auto');
      __overrideStyle(el, 'width', 'auto');
      __overrideStyle(el, 'max-width', 'none');
      __overrideStyle(el, 'z-index', 'auto');
      info.neutralized += 1;
    }
    return info;
  }

  function __neutralizeLegacyShellScroller(el) {
    if (!document.body) return;

    __overrideStyle(document.documentElement, 'height', 'auto');
    __overrideStyle(document.documentElement, 'max-height', 'none');
    __overrideStyle(document.documentElement, 'overflow', 'visible');
    __overrideStyle(document.documentElement, 'overflow-y', 'visible');
    __overrideStyle(document.body, 'height', 'auto');
    __overrideStyle(document.body, 'max-height', 'none');
    __overrideStyle(document.body, 'overflow', 'visible');
    __overrideStyle(document.body, 'overflow-y', 'visible');

    var current = el;
    while (current && current instanceof HTMLElement) {
      var rect = current.getBoundingClientRect();
      var style = window.getComputedStyle(current);
      var nearViewportHeight = Math.abs(rect.height - window.innerHeight) <= Math.max(48, window.innerHeight * 0.12);
      var constrainsShell = current === el
        || nearViewportHeight
        || style.overflow === 'hidden'
        || style.overflowY === 'hidden'
        || style.display === 'flex'
        || style.display === 'grid';

      if (constrainsShell) {
        __overrideStyle(current, 'height', 'auto');
        __overrideStyle(current, 'max-height', 'none');
        __overrideStyle(current, 'overflow', 'visible');
        __overrideStyle(current, 'overflow-y', 'visible');
      }

      if (current === document.body) break;
      current = current.parentElement;
    }
  }

  function __undoLegacyFixedHeightShells() {
    if (!document.body) return;

    var elements = Array.prototype.slice.call(document.body.querySelectorAll('*'));
    var candidates = elements.filter(__isLegacyShellScroller);
    candidates.sort(function(a, b) { return __elementDepth(a) - __elementDepth(b); });

    var handled = [];
    for (var i = 0; i < candidates.length; i += 1) {
      var candidate = candidates[i];
      var alreadyHandled = handled.some(function(parent) { return parent.contains(candidate); });
      if (alreadyHandled) continue;
      __neutralizeLegacyShellScroller(candidate);
      handled.push(candidate);
    }
  }

  function __measureLayoutHeight() {
    if (!document.body) return 0;

    __undoLegacyFixedHeightShells();
    var fixedChromeInfo = __neutralizeRootFixedChrome();

    var docEl = document.documentElement;
    var body = document.body;
    var viewportHeight = window.innerHeight || 0;
    var maxBottom = 0;
    var nodes = Array.prototype.slice.call(body.querySelectorAll('*'));
    var skippedFixed = 0;
    var skippedFixedDescendants = 0;
    var skippedViewportShells = 0;
    var fixedRoots = [];

    for (var i = 0; i < nodes.length; i += 1) {
      var el = nodes[i];
      if (!(el instanceof HTMLElement) || !__isVisibleElement(el)) continue;
      var style = window.getComputedStyle(el);
      if (style.position === 'fixed') {
        skippedFixed += 1;
        fixedRoots.push(el);
        continue;
      }
      var insideFixedRoot = false;
      for (var fixedIndex = 0; fixedIndex < fixedRoots.length; fixedIndex += 1) {
        if (fixedRoots[fixedIndex].contains(el)) {
          insideFixedRoot = true;
          break;
        }
      }
      if (insideFixedRoot) {
        skippedFixedDescendants += 1;
        continue;
      }
      var rect = el.getBoundingClientRect();
      if (__isViewportSizedShell(el, style, rect, viewportHeight)) {
        skippedViewportShells += 1;
        continue;
      }
      maxBottom = Math.max(maxBottom, rect.bottom + window.scrollY);
    }

    var bodyStyle = window.getComputedStyle(body);
    var bodyPaddingBottom = parseFloat(bodyStyle.paddingBottom || '0') || 0;
    var bodyMarginBottom = parseFloat(bodyStyle.marginBottom || '0') || 0;
    var maxBottomTracksViewport = maxBottom > 0 && Math.abs(maxBottom - viewportHeight) <= 4;
    var elementBottomSpace = maxBottomTracksViewport ? 0 : bodyPaddingBottom + bodyMarginBottom;
    var intrinsicFromElements = maxBottom > 0 ? maxBottom + elementBottomSpace : 0;
    var rootBottomSpace = bodyPaddingBottom + bodyMarginBottom;
    var rejectedHeights = [];

    function __rejectViewportFeedbackHeight(label, value) {
      if (!value || value <= viewportHeight + 2) return 0;
      if (intrinsicFromElements <= 0 || rootBottomSpace <= 2) return value;

      var tracksViewportPlusRootSpace = Math.abs(value - (viewportHeight + rootBottomSpace)) <= 4;
      if (tracksViewportPlusRootSpace && value > intrinsicFromElements + 2) {
        rejectedHeights.push(label + '=' + Math.round(value));
        return 0;
      }

      return value;
    }

    var rawBodyScroll = body.scrollHeight;
    var rawDocScroll = docEl.scrollHeight;
    var rawBodyOffset = body.offsetHeight;
    var rawDocOffset = docEl.offsetHeight;
    var intrinsicFromBodyScroll = __rejectViewportFeedbackHeight('bodyScroll', rawBodyScroll);
    var intrinsicFromDocScroll = __rejectViewportFeedbackHeight('docScroll', rawDocScroll);
    var intrinsicFromBodyOffset = __rejectViewportFeedbackHeight('bodyOffset', rawBodyOffset);
    var intrinsicFromDocOffset = __rejectViewportFeedbackHeight('docOffset', rawDocOffset);

    var measuredHeight = Math.ceil(Math.max(
      intrinsicFromElements,
      intrinsicFromBodyScroll,
      intrinsicFromDocScroll,
      intrinsicFromBodyOffset,
      intrinsicFromDocOffset
    ));

    __lastLayoutDebug = {
      iw: window.innerWidth || 0,
      ih: viewportHeight,
      vvH: window.visualViewport ? Math.round(window.visualViewport.height || 0) : null,
      scrollY: Math.round(window.scrollY || 0),
      nodes: nodes.length,
      skippedFixed: skippedFixed,
      skippedFixedDescendants: skippedFixedDescendants,
      skippedViewportShells: skippedViewportShells,
      fixedChromeChecked: fixedChromeInfo.checked,
      fixedChromeMatched: fixedChromeInfo.matched,
      fixedChromeNeutralized: fixedChromeInfo.neutralized,
      maxBottom: Math.round(maxBottom),
      maxBottomTracksViewport: maxBottomTracksViewport,
      bodyPaddingBottom: bodyPaddingBottom,
      bodyMarginBottom: bodyMarginBottom,
      rootBottomSpace: rootBottomSpace,
      elementBottomSpace: elementBottomSpace,
      intrinsicFromElements: Math.round(intrinsicFromElements),
      rawBodyScroll: rawBodyScroll,
      rawDocScroll: rawDocScroll,
      rawBodyOffset: rawBodyOffset,
      rawDocOffset: rawDocOffset,
      usedBodyScroll: intrinsicFromBodyScroll,
      usedDocScroll: intrinsicFromDocScroll,
      usedBodyOffset: intrinsicFromBodyOffset,
      usedDocOffset: intrinsicFromDocOffset,
      rejected: rejectedHeights.join(',') || 'none',
      measuredHeight: measuredHeight
    };

    return measuredHeight;
  }

  function __reportLayoutMetrics() {
    var nextHeight = __measureLayoutHeight();
    if (!Number.isFinite(nextHeight) || nextHeight <= 0) return;
    if (Math.abs(nextHeight - __lastReportedLayoutHeight) < 2) return;
    __lastReportedLayoutHeight = nextHeight;
    window.parent.postMessage({
      type: 'llambada:layoutMetrics',
      height: nextHeight,
      debug: __lastLayoutDebug,
    }, '*');
  }

  function __scheduleLayoutMeasurement() {
    if (__layoutScheduled) return;
    __layoutScheduled = true;
    window.requestAnimationFrame(function() {
      __layoutScheduled = false;
      __reportLayoutMetrics();
    });
  }

  function __startAutoHeightTracking() {
    if (__layoutTrackingStarted) return;
    __layoutTrackingStarted = true;

    if (typeof ResizeObserver !== 'undefined') {
      var resizeObserver = new ResizeObserver(function() {
        __scheduleLayoutMeasurement();
      });
      if (document.documentElement) resizeObserver.observe(document.documentElement);
      if (document.body) resizeObserver.observe(document.body);
    }

    if (typeof MutationObserver !== 'undefined' && document.body) {
      var mutationObserver = new MutationObserver(function() {
        __scheduleLayoutMeasurement();
      });
      mutationObserver.observe(document.body, {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true,
      });
    }

    window.addEventListener('resize', __scheduleLayoutMeasurement);
    window.addEventListener('load', __scheduleLayoutMeasurement);
    document.addEventListener('load', __scheduleLayoutMeasurement, true);
    document.addEventListener('transitionend', __scheduleLayoutMeasurement, true);
    document.addEventListener('animationend', __scheduleLayoutMeasurement, true);

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(__scheduleLayoutMeasurement).catch(function() {});
    }

    __scheduleLayoutMeasurement();
    window.setTimeout(__scheduleLayoutMeasurement, 50);
    window.setTimeout(__scheduleLayoutMeasurement, 250);
  }

  function notifyRuntimeReady() {
    __startAutoHeightTracking();
    __scheduleLayoutMeasurement();
    window.parent.postMessage({ type: 'llambada:runtimeReady' }, '*');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', notifyRuntimeReady, { once: true });
  } else {
    setTimeout(notifyRuntimeReady, 0);
  }

  // =========================================================================
  // Structured jobError handler
  // Dispatches on error.code (not keyword matching) using the injected
  // __ERROR_CODES, __ERROR_CATEGORY, and __ERROR_DISPLAY catalogs.
  // =========================================================================

  // Shared modal styles (injected once)
  var __modalStyleInjected = false;
  function ensureModalStyles() {
    if (__modalStyleInjected) return;
    __modalStyleInjected = true;
    var style = document.createElement('style');
    style.textContent = [
      '.llambada-modal-overlay { position:fixed; inset:0; background:var(--color-overlay,rgba(0,0,0,0.5)); display:flex; align-items:center; justify-content:center; z-index:10001; padding:1rem; }',
      '.llambada-modal-content { background:var(--color-surface,#fff); border-radius:12px; padding:2rem; max-width:400px; width:100%; text-align:center; position:relative; box-shadow:var(--shadow-xl,0 20px 25px -5px rgba(0,0,0,0.1)); }',
      '.llambada-modal-close { position:absolute; top:0.75rem; right:0.75rem; background:none; border:none; font-size:1.5rem; color:var(--color-text-muted,#999); cursor:pointer; padding:0.25rem; line-height:1; border-radius:4px; }',
      '.llambada-modal-close:hover { background:var(--color-surface-hover,#f5f5f5); color:var(--color-text,#333); }',
      '.llambada-modal-icon { width:64px; height:64px; margin:0 auto 1rem; border-radius:50%; display:flex; align-items:center; justify-content:center; }',
      '.llambada-modal-icon--warning { background:var(--color-warning-bg,#fff8e1); color:var(--color-warning,#f59e0b); }',
      '.llambada-modal-icon--error { background:var(--color-error-bg,#ffebee); color:var(--color-error,#c62828); }',
      '.llambada-modal-icon--auth { background:var(--color-info-bg,#e3f2fd); color:var(--color-info,#1565c0); }',
      '.llambada-modal-title { font-size:1.25rem; font-weight:600; color:var(--color-text,#333); margin:0 0 0.75rem; }',
      '.llambada-modal-message { font-size:0.95rem; color:var(--color-text-secondary,#666); margin:0 0 1.5rem; line-height:1.5; }',
      '.llambada-modal-actions { display:flex; flex-direction:column; gap:0.75rem; }',
      '.llambada-modal-btn-primary { display:inline-block; background:var(--color-primary,#2563eb); color:#fff; padding:0.75rem 1.5rem; border-radius:8px; font-weight:500; text-decoration:none; border:none; cursor:pointer; font-size:0.95rem; }',
      '.llambada-modal-btn-primary:hover { background:var(--color-primary-hover,#1d4ed8); text-decoration:none; }',
      '.llambada-modal-btn-secondary { background:none; border:1px solid var(--color-border,#ddd); color:var(--color-text-secondary,#666); padding:0.75rem 1.5rem; border-radius:8px; font-weight:500; cursor:pointer; font-size:0.95rem; }',
      '.llambada-modal-btn-secondary:hover { background:var(--color-surface-hover,#f5f5f5); }',
    ].join('\\n');
    document.head.appendChild(style);
  }

  var __warningSvg = '<svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>';
  var __lockSvg = '<svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>';
  var __errorSvg = '<svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="m15 9-6 6"/><path d="m9 9 6 6"/></svg>';

  function showErrorModal(title, message, options) {
    ensureModalStyles();
    var iconClass = options.iconClass || 'llambada-modal-icon--warning';
    var iconSvg = options.iconSvg || __warningSvg;
    
    var modal = document.getElementById('llambada-error-modal');
    if (modal) modal.remove();
    
    modal = document.createElement('div');
    modal.id = 'llambada-error-modal';
    
    var actionsHtml = '';
    if (options.showUpgrade) {
      actionsHtml += '<a href="/pricing" class="llambada-modal-btn-primary">Upgrade Your Plan</a>';
    }
    if (options.showSignIn) {
      actionsHtml += '<a href="/login" class="llambada-modal-btn-primary">Sign In</a>';
    }
    actionsHtml += '<button class="llambada-modal-btn-secondary">Close</button>';
    
    modal.innerHTML = '<div class="llambada-modal-overlay">' +
      '<div class="llambada-modal-content">' +
        '<button class="llambada-modal-close" aria-label="Close">&times;</button>' +
        '<div class="llambada-modal-icon ' + iconClass + '">' + iconSvg + '</div>' +
        '<h2 class="llambada-modal-title">' + title + '</h2>' +
        '<p class="llambada-modal-message">' + message + '</p>' +
        '<div class="llambada-modal-actions">' + actionsHtml + '</div>' +
      '</div>' +
    '</div>';
    
    document.body.appendChild(modal);
    
    // Close handlers
    function closeModal() { modal.remove(); }
    modal.querySelector('.llambada-modal-close').addEventListener('click', closeModal);
    modal.querySelector('.llambada-modal-btn-secondary').addEventListener('click', closeModal);
    modal.querySelector('.llambada-modal-overlay').addEventListener('click', function(evt) {
      if (evt.target === this) closeModal();
    });
  }

  function showErrorToast(message) {
    // For long technical errors, show a simplified message
    var displayMessage = message;
    if (message.includes('Traceback') || message.includes('raise_for_status') || message.length > 200) {
      displayMessage = 'Something went wrong. Please try again.';
    }
    emitPlatformToast(displayMessage, 'error', 5000);
  }

  window.addEventListener('jobError', function(e) {
    console.log('[jobError] Event received:', e.detail);
    var code = e.detail.code || 'UNKNOWN';
    var message = e.detail.message || '';
    var details = e.detail.details || {};
    
    // Look up the error in the catalog
    var category = __ERROR_CATEGORY[code];
    var display = __ERROR_DISPLAY[code];
    
    if (!category || !display) {
      // Unknown error code — not in catalog
      category = 'unknown';
      display = __ERROR_DISPLAY['UNKNOWN'];
      // Report unknown error to parent for logging to database
      window.parent.postMessage({
        type: 'llambada:logUnknownError',
        errorCode: code,
        errorMessage: message,
        errorDetails: details
      }, '*');
    }
    
    // Use the backend message if available (it may contain specific details
    // like actual limits), falling back to the catalog default
    var displayMessage = message || display.defaultMessage;
    
    switch (category) {
      case 'limit':
        showErrorModal(display.title, displayMessage, {
          iconClass: 'llambada-modal-icon--warning',
          iconSvg: __warningSvg,
          showUpgrade: display.showUpgrade,
          showSignIn: display.showSignIn,
        });
        break;
        
      case 'auth':
        showErrorModal(display.title, displayMessage, {
          iconClass: 'llambada-modal-icon--auth',
          iconSvg: __lockSvg,
          showUpgrade: display.showUpgrade,
          showSignIn: display.showSignIn,
        });
        break;
      
      case 'execution':
      case 'infrastructure':
        showErrorToast(displayMessage);
        break;
        
      case 'unknown':
      default:
        showErrorModal(display.title, 'An unexpected error occurred. Please contact support if this persists.', {
          iconClass: 'llambada-modal-icon--error',
          iconSvg: __errorSvg,
          showUpgrade: false,
          showSignIn: false,
        });
        break;
    }
  });

  // Prevent navigation/close when platform API calls are still in-flight
  window.addEventListener('beforeunload', function(e) {
    if (__pendingRequests > 0) {
      e.preventDefault();
      e.returnValue = '';
    }
  });

})();
</script>
`;
}


export default function FrontendPreview({
  html,
  frontendFiles = {},
  onSubmit,
  isProcessing = false,
  isComplete = false,
  backendError = null,
  buildStatus = null,
  runLogs = null,
  runStatus = 'idle',
  runProgress: _runProgress = null,
  streamingLogs = null,
  buildLogs = null,
  buildLogsLoading = false,
  buildLogsError = null,
  buildLogsDiagnostics = null,
  isImageSizeLimitError = false,
  lastBuildAt = null,
  lastBuildId = null,
  onFetchBuildLogs,
  onTriggerRebuild,
  publicMode = false,
  projectId = '',
  userContext = {
    id: null,
    email: null,
    displayName: null,
    tier: 'free' as const,
    locale: 'en',
    creditsAvailable: 0,
    creditsLoaded: false,
    creditCosts: {},
    maxPayloadBytes: MINIAPP_MAX_PAYLOAD_BYTES.free,
    embeddingBatchLimit: EMBEDDING_BATCH_LIMITS.free,
  },
  familyConfig = null,
  onFrontendError,
  isBlocked = false,
  blockedMessage = 'Preview is temporarily unavailable',
  appUrl: appUrlProp,
  sessionToken,
  getSessionToken,
  getTurnstileToken,
  containerRef,
  projectUpdatedAt = null,
}: FrontendPreviewProps) {
  const { theme } = useTheme();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const blobUrlRef = useRef<string | null>(null);
  const staleBlobUrlsRef = useRef<Set<string>>(new Set());
  // Store the last-built full HTML so we can recreate the blob URL if the
  // browser invalidates it (e.g. after tab freeze / background discard).
  const lastBuiltHtmlRef = useRef<string | null>(null);
  const iframeReadyWaitersRef = useRef<Array<() => void>>([]);
  const sessionTokenRef = useRef(sessionToken);
  useEffect(() => { sessionTokenRef.current = sessionToken; }, [sessionToken]);
  const getSessionTokenRef = useRef(getSessionToken);
  useEffect(() => { getSessionTokenRef.current = getSessionToken; }, [getSessionToken]);
  const getTurnstileTokenRef = useRef(getTurnstileToken);
  useEffect(() => { getTurnstileTokenRef.current = getTurnstileToken; }, [getTurnstileToken]);
  // Keep a ref for userContext so the iframe-creation effect can read the latest
  // value without having userContext.id in its dependency array. Auth state changes
  // are delivered via postMessage (llambada:userContextUpdate), so the iframe
  // does NOT need to be recreated when the user logs in/out.
  const userContextRef = useRef(userContext);
  useEffect(() => { userContextRef.current = userContext; }, [userContext]);
  // Keep a ref for frontendFiles so the iframe-creation effect can read the object
  // without re-running when a new (but content-identical) object reference is created.
  const frontendFilesRef = useRef(frontendFiles);
  useEffect(() => { frontendFilesRef.current = frontendFiles; }, [frontendFiles]);
  const frontendFilesKey = JSON.stringify(frontendFiles);

  // --- Tab-recovery state: cache output files so they survive blob URL rebuild ---
  const cachedOutputFilesRef = useRef<Map<string, ArrayBuffer>>(new Map());
  const cachedInputFilesRef = useRef<Map<string, { data: ArrayBuffer; contentType: string }>>(new Map());
  const lastOutputFilesMetaRef = useRef<Array<{ filename: string; size: number }>>([]);
  // Refs for props used inside the [] -dependency visibility handler (avoids stale closures)
  const isCompleteRef = useRef(isComplete);
  useEffect(() => { isCompleteRef.current = isComplete; }, [isComplete]);
  const isProcessingRef = useRef(isProcessing);
  useEffect(() => { isProcessingRef.current = isProcessing; }, [isProcessing]);
  const backendErrorRef = useRef(backendError);
  useEffect(() => { backendErrorRef.current = backendError; }, [backendError]);

  const [isExpanded, setIsExpanded] = useState(false);
  const [key, setKey] = useState(0);
  const [errors, setErrors] = useState<CapturedError[]>([]);
  const [showErrors, setShowErrors] = useState(false);
  const [copied, setCopied] = useState(false);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [isRecovering, setIsRecovering] = useState(false);
  const [hostToast, setHostToast] = useState<HostToastState | null>(null);
  const hostToastTimeoutRef = useRef<number | null>(null);
  const iframeLoadedRef = useRef(false);
  useEffect(() => { iframeLoadedRef.current = iframeLoaded; }, [iframeLoaded]);

  // The iframe runtime reports measured layout height after it normalizes
  // legacy fixed-height shells inside the mini-app.
  const [iframeHeight, setIframeHeight] = useState(600);
  const applyIframeHeight = useCallback((nextHeight: number, _runtimeDebug?: Record<string, unknown> | null) => {
    if (!Number.isFinite(nextHeight)) return;
    const normalizedHeight = Math.max(120, Math.ceil(nextHeight));
    setIframeHeight((prev) => {
      return Math.abs(prev - normalizedHeight) < 50 ? prev : normalizedHeight;
    });
  }, []);

  // Pending-request guard: prevents accidental navigation while mini-app API calls are in-flight
  const pendingRequestsRef = useRef(0);
  const handleBeforeUnload = useCallback((e: BeforeUnloadEvent) => {
    if (pendingRequestsRef.current > 0) {
      e.preventDefault();
      e.returnValue = '';
    }
  }, []);
  useEffect(() => {
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [handleBeforeUnload]);

  // Logs panel state
  const [showLogs, setShowLogs] = useState(false);
  const [activeLogTab, setActiveLogTab] = useState<'deployment' | 'backend' | 'frontend'>('deployment');
  
  // Cleanup blob URL on unmount to prevent memory leak
  const flushStaleBlobUrls = useCallback(() => {
    for (const staleUrl of staleBlobUrlsRef.current) {
      URL.revokeObjectURL(staleUrl);
    }
    staleBlobUrlsRef.current.clear();
  }, []);

  const markBlobUrlStale = useCallback((blobUrl: string | null) => {
    if (!blobUrl) return;
    staleBlobUrlsRef.current.add(blobUrl);
  }, []);

  useEffect(() => {
    return () => {
      iframeReadyWaitersRef.current = [];
      flushStaleBlobUrls();
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
      }
    };
  }, [flushStaleBlobUrls]);

  const resolveIframeReady = useCallback(() => {
    setIframeLoaded(true);
    flushStaleBlobUrls();
    const waiters = iframeReadyWaitersRef.current;
    iframeReadyWaitersRef.current = [];
    waiters.forEach((resolve) => resolve());
  }, [flushStaleBlobUrls]);

  const waitForIframeReady = useCallback((timeoutMs = 15000) => {
    if (iframeLoadedRef.current) return Promise.resolve();

    return new Promise<void>((resolve, reject) => {
      const onReady = () => {
        window.clearTimeout(timeoutId);
        resolve();
      };

      const timeoutId = window.setTimeout(() => {
        iframeReadyWaitersRef.current = iframeReadyWaitersRef.current.filter((entry) => entry !== onReady);
        reject(new Error('Iframe runtime ready timeout'));
      }, timeoutMs);

      iframeReadyWaitersRef.current.push(onReady);
    });
  }, []);
  
  // Auto-fetch build logs when panel opens for first time
  useEffect(() => {
    // Don't retry if there's already an error
    if (showLogs && activeLogTab === 'deployment' && !buildLogs && !buildLogsLoading && !buildLogsError && lastBuildId && onFetchBuildLogs) {
      onFetchBuildLogs();
    }
  }, [showLogs, activeLogTab, buildLogs, buildLogsLoading, buildLogsError, lastBuildId, onFetchBuildLogs]);
  
  // Auto-show logs and switch to backend tab when backend starts running
  useEffect(() => {
    if (runStatus === 'running' || runStatus === 'authenticating') {
      setShowLogs(true);
      setActiveLogTab('backend');
    }
  }, [runStatus]);

  // Auto-show logs and switch to backend tab when there's a run error
  useEffect(() => {
    if (runLogs && (runLogs.exitCode !== 0 || runLogs.hasAppError)) {
      setShowLogs(true);
      setActiveLogTab('backend');
    }
  }, [runLogs]);
  
  // Auto-show logs and switch to deployment tab when build fails
  useEffect(() => {
    if (buildStatus === 'failed') {
      setShowLogs(true);
      setActiveLogTab('deployment');
      // Don't retry if there's already an error or logs
      if (onFetchBuildLogs && !buildLogs && !buildLogsLoading && !buildLogsError) {
        onFetchBuildLogs();
      }
    }
  }, [buildStatus, buildLogs, buildLogsLoading, buildLogsError, onFetchBuildLogs]);
  
  // Auto-show logs panel when build is in progress for real-time streaming
  useEffect(() => {
    if (buildStatus === 'building') {
      setShowLogs(true);
      setActiveLogTab('deployment');
    }
  }, [buildStatus]);
  
  // Auto-show logs and switch to frontend tab when there are frontend JS errors
  useEffect(() => {
    if (errors.length > 0) {
      setShowLogs(true);
      setActiveLogTab('frontend');
    }
  }, [errors]);

  // Debug: log buildStatus changes
  useEffect(() => {
    console.log('FrontendPreview buildStatus:', buildStatus);
  }, [buildStatus]);

  // Clear errors on refresh
  const handleRefresh = () => {
    setKey((k) => k + 1);
    setErrors([]);
    setShowErrors(false);
    setIframeLoaded(false);
  };

  // Format error for display/copy
  const formatErrorForCopy = (error: CapturedError): string => {
    let result = `[${error.type.toUpperCase()}] ${error.message}`;
    if (error.source) {
      result += `\n  at ${error.source}`;
      if (error.lineno) {
        result += `:${error.lineno}`;
        if (error.colno) result += `:${error.colno}`;
      }
    }
    if (error.stack) {
      result += `\n\nStack trace:\n${error.stack}`;
    }
    return result;
  };

  // Get all errors formatted for clipboard
  const getAllErrorsForCopy = (): string => {
    const parts: string[] = [];
    
    if (backendError) {
      parts.push(`[BACKEND ERROR]\n${backendError}`);
    }
    
    if (errors.length > 0) {
      parts.push(`[FRONTEND ERRORS]\n${errors.map(formatErrorForCopy).join('\n\n---\n\n')}`);
    }
    
    return parts.join('\n\n========================================\n\n');
  };

  // Copy errors to clipboard
  const handleCopyErrors = async () => {
    const text = getAllErrorsForCopy();
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const clearHostToastTimeout = () => {
    if (hostToastTimeoutRef.current !== null) {
      window.clearTimeout(hostToastTimeoutRef.current);
      hostToastTimeoutRef.current = null;
    }
  };

  const showHostToast = (message: unknown, tone: unknown, duration: unknown) => {
    const text = typeof message === 'string'
      ? message.trim()
      : message == null
        ? ''
        : String(message).trim();
    if (!text) return;

    const normalizedTone: HostToastTone =
      tone === 'success' || tone === 'error' || tone === 'warning' ? tone : '';
    const timeoutMs = typeof duration === 'number' && Number.isFinite(duration)
      ? Math.min(Math.max(duration, 1000), 15000)
      : 3000;

    clearHostToastTimeout();
    setHostToast({ message: text, tone: normalizedTone });
    hostToastTimeoutRef.current = window.setTimeout(() => {
      setHostToast(null);
      hostToastTimeoutRef.current = null;
    }, timeoutMs);
  };

  useEffect(() => {
    return () => {
      clearHostToastTimeout();
    };
  }, []);

  // Total error count (backend + frontend)
  const totalErrors = errors.length + (backendError ? 1 : 0);

  // Track common CSS loading
  const [commonCss, setCommonCss] = useState<string | null>(null);

  useEffect(() => {
    if (!commonCss) {
      getCommonCss(projectUpdatedAt).then(setCommonCss);
    }
  }, [commonCss, projectUpdatedAt]);

  // Track initial theme for iframe setup
  const initialThemeRef = useRef(theme);
  
  // Update ref when theme changes (for new iframe loads)
  useEffect(() => {
    initialThemeRef.current = theme;
  }, [theme]);

  // Reset iframeLoaded when content changes (iframe will reload)
  useEffect(() => {
    setIframeLoaded(false);
    setIframeHeight(600);
    iframeReadyWaitersRef.current = [];
  }, [html, commonCss, frontendFilesKey, projectId, JSON.stringify(familyConfig?.params), familyConfig?.description]);

  // Inject runtime and HTML into iframe (only when content changes, NOT on theme change)
  useEffect(() => {
    if (!iframeRef.current || !commonCss) return;

    let modifiedHtml = html;
    
    // Inject theme class into <html> tag to sync with main app theme (use current theme at load time)
    if (initialThemeRef.current === 'dark') {
      if (modifiedHtml.includes('<html')) {
        modifiedHtml = modifiedHtml.replace(/<html([^>]*)>/, (match, attrs) => {
          if (attrs.includes('class=')) {
            return match.replace(/class=["']([^"']*)["']/, 'class="$1 dark"');
          } else {
            return `<html${attrs} class="dark">`;
          }
        });
      }
    }
    
    // Inject common.css styles inline for consistent styling
    // If the HTML has a common.css link, replace it with inline styles
    // If not, inject the styles into <head> anyway
    if (modifiedHtml.includes('href="common.css"') || modifiedHtml.includes("href='common.css'")) {
      modifiedHtml = modifiedHtml.replace(/<link[^>]*href=["']common\.css["'][^>]*>/gi, `<style id="common-css">${commonCss}</style>`);
    } else {
      // No common.css link found - inject styles into <head>
      const commonCssStyle = `<style id="common-css">${commonCss}</style>`;
      if (modifiedHtml.includes('</head>')) {
        modifiedHtml = modifiedHtml.replace('</head>', commonCssStyle + '</head>');
      } else if (modifiedHtml.includes('<body')) {
        modifiedHtml = modifiedHtml.replace(/<body([^>]*)>/, commonCssStyle + '<body$1>');
      } else {
        // No head or body tag - prepend styles
        modifiedHtml = commonCssStyle + modifiedHtml;
      }
    }
    
    // For family forks, replace the <title> tag content with the interpolated project name
    // so the iframe's document.title reflects the parametrized fork name (e.g. "Markdown to PDF Converter")
    if (familyConfig?.title) {
      modifiedHtml = modifiedHtml.replace(/<title>[^<]*<\/title>/i, `<title>${familyConfig.title}</title>`);
    }

    // Generate runtime with embedded frontend files and data storage config
    console.log('[FrontendPreview] Generating runtime with frontendFiles:', Object.keys(frontendFiles));
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
    const appUrl = appUrlProp || (projectId ? (window.location.origin + '/project/' + projectId) : window.location.href.split('?')[0]);
    const urlParams: Record<string, string> = {};
    new URLSearchParams(window.location.search).forEach((value, key) => { urlParams[key] = value; });
    const runtime = getLlambadaRuntime(frontendFilesRef.current, supabaseUrl, projectId, userContextRef.current, familyConfig, appUrl, urlParams, publicMode, sessionTokenRef.current || null);
    
    // Inject the runtime before </head> or at the start of <body>
    if (html.includes('</head>')) {
      modifiedHtml = modifiedHtml.replace('</head>', runtime + '</head>');
    } else if (html.includes('<body>')) {
      modifiedHtml = modifiedHtml.replace('<body>', '<body>' + runtime);
    } else {
      modifiedHtml = runtime + modifiedHtml;
    }

    const iframe = iframeRef.current;
    const previousBlobUrl = blobUrlRef.current;

    // Create blob URL - this allows external scripts/images to load (unlike srcdoc)
    const blob = new Blob([modifiedHtml], { type: 'text/html' });
    const blobUrl = URL.createObjectURL(blob);
    blobUrlRef.current = blobUrl;
    lastBuiltHtmlRef.current = modifiedHtml;
    markBlobUrlStale(previousBlobUrl);
    setIframeLoaded(false);
    iframe.src = blobUrl;
    // userContext is NOT in the dependency array — auth state changes are delivered
    // to the iframe via postMessage (llambada:userContextUpdate) without restarting
    // the iframe. This prevents destroying user state (open file pickers, form data,
    // scroll position, etc.) when auth resolves after initial render.
  }, [html, key, commonCss, frontendFilesKey, projectId, appUrlProp, JSON.stringify(familyConfig?.params), familyConfig?.description, familyConfig?.title, markBlobUrlStale]);
  
  // ── Tab-recovery: detect dead blob URLs and rebuild with cached output files ──
  //
  // When the browser freezes/discards the tab, the blob URL data may be
  // garbage-collected.  On return the user would see a broken iframe.
  //
  // Strategy:
  //   hidden  → show a recovery overlay immediately (covers the potentially dead iframe)
  //   visible → ping the iframe (1s timeout — iframe JS may still be unfreezing)
  //             • pong received  → iframe alive, hide overlay, re-push completion/error
  //             • no pong        → rebuild blob URL, wait for onLoad, re-sync cached
  //                                output files, re-send completion/error, hide overlay
  useEffect(() => {
    let recoveryInProgress = false;
    let disposed = false;

    const getRecoveredOutputFilesMeta = () => {
      const explicitMeta = lastOutputFilesMetaRef.current.filter((file) => file.filename !== 'progress.json');
      if (explicitMeta.length > 0) return explicitMeta;

      const inferred: Array<{ filename: string; size: number }> = [];
      for (const [path, data] of cachedOutputFilesRef.current.entries()) {
        if (path === 'progress.json') continue;
        inferred.push({ filename: path, size: data.byteLength });
      }
      return inferred;
    };

    const hydrateCachedRunIntoMemory = async () => {
      if (!projectId) return null;

      const cached = await loadCachedRun(projectId);
      if (!cached) return null;

      if (cached.files.length > 0 && cachedOutputFilesRef.current.size === 0) {
        for (const { path, data } of cached.files) {
          cachedOutputFilesRef.current.set(path, data.slice(0));
        }
      }

      if (cached.inputFiles && cached.inputFiles.length > 0 && cachedInputFilesRef.current.size === 0) {
        for (const { path, data, contentType } of cached.inputFiles) {
          cachedInputFilesRef.current.set(path, { data: data.slice(0), contentType });
        }
      }

      if (cached.outputFilesMeta.length > 0 && lastOutputFilesMetaRef.current.length === 0) {
        lastOutputFilesMetaRef.current = cached.outputFilesMeta;
      }

      return cached;
    };

    const getSerializedInputFiles = () => {
      const result: Array<{ filename: string; data: number[]; contentType: string }> = [];
      for (const [filename, entry] of cachedInputFilesRef.current.entries()) {
        result.push({
          filename,
          data: Array.from(new Uint8Array(entry.data)),
          contentType: entry.contentType,
        });
      }
      return result;
    };

    const rePushState = (
      iframeWin: WindowProxy | null,
      cachedRun?: Awaited<ReturnType<typeof loadCachedRun>> | null,
    ) => {
      if (!iframeWin) return;
      const recoveredOutputFiles = getRecoveredOutputFilesMeta();
      const inputFileData = getSerializedInputFiles();
      if (isCompleteRef.current && !isProcessingRef.current) {
        iframeWin.postMessage({
          type: 'llambada:complete',
          outputFiles: recoveredOutputFiles,
          __inputFileData: inputFileData,
          restored: true,
        }, '*');
      } else if (backendErrorRef.current) {
        iframeWin.postMessage({
          type: 'llambada:error',
          code: 'EXECUTION_FAILED',
          message: backendErrorRef.current,
          restored: true,
        }, '*');
      } else if (recoveredOutputFiles.length > 0) {
        iframeWin.postMessage({
          type: 'llambada:complete',
          outputFiles: recoveredOutputFiles,
          __inputFileData: inputFileData,
          restored: true,
        }, '*');
      } else if (cachedRun?.isComplete) {
        iframeWin.postMessage({
          type: 'llambada:complete',
          outputFiles: cachedRun.outputFilesMeta.length > 0
            ? cachedRun.outputFilesMeta
            : recoveredOutputFiles,
          __inputFileData: inputFileData,
          restored: true,
        }, '*');
      } else if (cachedRun?.error) {
        iframeWin.postMessage({
          type: 'llambada:error',
          code: 'EXECUTION_FAILED',
          message: cachedRun.error,
          restored: true,
        }, '*');
      }
    };

    // Hydrate the in-memory cache (if empty) from IndexedDB, then sync all
    // cached output files into the iframe's __fileStore via postMessage.
    // Returns the cachedRun (if hydrated from IndexedDB) for rePushState.
    const hydrateAndSyncFiles = async (
      iframeWin: WindowProxy | null,
    ): Promise<Awaited<ReturnType<typeof loadCachedRun>> | null> => {
      const cachedRun = cachedOutputFilesRef.current.size > 0
        ? null
        : await hydrateCachedRunIntoMemory();
      const files = cachedOutputFilesRef.current;
      if (files.size > 0 && iframeWin) {
        console.log('[FrontendPreview] Re-syncing', files.size, 'cached output files');
        for (const [path, data] of files.entries()) {
          try {
            await new Promise<void>((resolve, reject) => {
              const timeout = setTimeout(() => {
                window.removeEventListener('message', handler);
                reject(new Error('Sync timeout'));
              }, 10000);
              const handler = (e: MessageEvent) => {
                if (e.data?.type === 'llambada:syncComplete' && e.data.path === path) {
                  clearTimeout(timeout);
                  window.removeEventListener('message', handler);
                  resolve();
                }
                if (e.data?.type === 'llambada:syncError' && e.data.path === path) {
                  clearTimeout(timeout);
                  window.removeEventListener('message', handler);
                  reject(new Error(e.data.error));
                }
              };
              window.addEventListener('message', handler);
              iframeWin.postMessage({
                type: 'llambada:syncFile',
                path,
                data: Array.from(new Uint8Array(data)),
              }, '*');
            });
          } catch (err) {
            console.error('[FrontendPreview] Failed to re-sync file during recovery:', path, err);
          }
        }
      } else if (cachedRun?.files.length === 0) {
        console.log('[FrontendPreview] Recovery found cached terminal state without output files');
      }
      return cachedRun;
    };

    const rebuildAndResync = async () => {
      const iframe = iframeRef.current;
      if (!iframe || !lastBuiltHtmlRef.current) {
        setIsRecovering(false);
        recoveryInProgress = false;
        return;
      }

      try {
        console.log('[FrontendPreview] Blob URL invalidated — rebuilding iframe and re-syncing files');

        // 1. Rebuild the blob URL — attach load listener BEFORE setting src
        //    to avoid a race where load fires before the listener is ready.
        const previousBlobUrl = blobUrlRef.current;
        const blob = new Blob([lastBuiltHtmlRef.current], { type: 'text/html' });
        const blobUrl = URL.createObjectURL(blob);
        blobUrlRef.current = blobUrl;
        markBlobUrlStale(previousBlobUrl);
        setIframeLoaded(false);
        iframeReadyWaitersRef.current = [];
        iframe.src = blobUrl;
        await waitForIframeReady(15000);

        // 2. Re-sync all cached output files.
        const cachedRun = await hydrateAndSyncFiles(iframe.contentWindow);

        // 3. Re-send completion or error signal (with input file data for media restoration)
        rePushState(iframe.contentWindow, cachedRun);

      } catch (err) {
        if (err instanceof Error && err.message === 'Iframe runtime ready timeout') {
          console.warn('[FrontendPreview] Iframe runtime did not signal ready during recovery');
        }
        console.error('[FrontendPreview] Recovery failed:', err);
      } finally {
        // Always hide overlay and unlock — never leave the spinner stuck
        setIsRecovering(false);
        recoveryInProgress = false;
      }
    };

    const handleHidden = () => {
      if (disposed) return;
      if (document.visibilityState === 'hidden') {
        // Immediately show overlay so the user never sees a broken iframe
        setIsRecovering(true);
      }
    };

    const handleVisibleReturn = () => {
      if (disposed) return;
      if (document.visibilityState !== 'visible') return;

      // --- visible ---
      if (recoveryInProgress) return;
      recoveryInProgress = true;

      const iframe = iframeRef.current;
      if (!iframe?.contentWindow || !lastBuiltHtmlRef.current) {
        setIsRecovering(false);
        recoveryInProgress = false;
        return;
      }

      // Ping the iframe — give it 1s since the browser may still be
      // unfreezing iframe JS after restoring the tab.
      let pongReceived = false;
      const onPong = (e: MessageEvent) => {
        if (e.data?.type === 'llambada:pong') {
          pongReceived = true;
        }
      };
      window.addEventListener('message', onPong);
      try {
        iframe.contentWindow.postMessage({ type: 'llambada:ping' }, '*');
      } catch {
        window.removeEventListener('message', onPong);
        rebuildAndResync();
        return;
      }

      setTimeout(() => {
        window.removeEventListener('message', onPong);
        if (pongReceived) {
          // Iframe is alive — re-sync cached output files defensively (the
          // browser may have evicted __fileStore entries during a long freeze),
          // then re-push completion/error state.
          (async () => {
            try {
              const cachedRun = await hydrateAndSyncFiles(iframe.contentWindow);
              rePushState(iframe.contentWindow, cachedRun);
            } catch (err) {
              console.error('[FrontendPreview] Pong recovery file sync failed:', err);
            } finally {
              setIsRecovering(false);
              recoveryInProgress = false;
            }
          })();
        } else {
          // Iframe is dead — full rebuild + re-sync
          rebuildAndResync();
        }
      }, 1000);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        handleHidden();
      } else {
        handleVisibleReturn();
      }
    };

    const handleFocus = () => {
      handleVisibleReturn();
    };

    const handlePageShow = () => {
      handleVisibleReturn();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('pageshow', handlePageShow);

    return () => {
      disposed = true;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [projectId, markBlobUrlStale, waitForIframeReady]);

  // Handle theme changes via postMessage (preserves iframe state)
  useEffect(() => {
    if (!iframeRef.current?.contentWindow) return;

    // Send theme change to iframe without reloading
    iframeRef.current.contentWindow.postMessage({
      type: 'llambada:themeChange',
      theme: theme
    }, '*');
  }, [theme]);

  // Send user context updates via postMessage when auth state changes
  // This ensures mini-apps always have current user info, even if initial load had stale data
  useEffect(() => {
    if (!iframeRef.current?.contentWindow || !iframeLoaded) return;
    
    // Send user context update to iframe
    iframeRef.current.contentWindow.postMessage({
      type: 'llambada:userContextUpdate',
      user: {
        id: userContext.id,
        email: userContext.email,
        displayName: userContext.displayName,
        tier: userContext.tier,
        locale: userContext.locale,
        creditsAvailable: userContext.creditsAvailable,
        creditsLoaded: userContext.creditsLoaded,
        creditCosts: userContext.creditCosts,
        maxPayloadBytes: userContext.maxPayloadBytes,
        embeddingBatchLimit: userContext.embeddingBatchLimit,
      }
    }, '*');
  }, [iframeLoaded, userContext.id, userContext.email, userContext.displayName, userContext.tier, userContext.locale, userContext.creditsAvailable, userContext.creditsLoaded, userContext.creditCosts, userContext.maxPayloadBytes, userContext.embeddingBatchLimit]);

  // Proactively push fresh auth token to iframe when session changes
  // This avoids the iframe needing to request a token refresh (which could cause state updates → iframe reload)
  useEffect(() => {
    if (!iframeRef.current?.contentWindow || !iframeLoaded) return;
    if (sessionToken) {
      iframeRef.current.contentWindow.postMessage({
        type: 'llambada:authToken',
        token: sessionToken
      }, '*');
    }
  }, [iframeLoaded, sessionToken]);

  // Handle messages from iframe
  useEffect(() => {
    const handleMessage = async (e: MessageEvent) => {
      const data = e.data;
      if (!data || !data.type) return;

      switch (data.type) {
        case 'llambada:submit':
          // Block submission if backend is building
          if (buildStatus === 'building') {
            iframeRef.current?.contentWindow?.postMessage({
              type: 'llambada:error',
              code: ERROR_CODES.BACKEND_BUILDING,
              message: 'Backend is currently rebuilding. Please wait 1-2 minutes and try again.',
            }, '*');
            return;
          }
          // Cache user-submitted files for tab-recovery (so the original input
          // can be restored alongside the output after an iframe rebuild).
          try {
            const entries = data.entries || [];
            cachedInputFilesRef.current.clear();
            for (const entry of entries) {
              if (entry.type === 'file' && entry.data) {
                const arr = new Uint8Array(entry.data);
                cachedInputFilesRef.current.set(
                  entry.filename || entry.key,
                  { data: arr.buffer.slice(0), contentType: entry.contentType || 'application/octet-stream' },
                );
              }
            }
          } catch (e) {
            console.warn('[FrontendPreview] Failed to cache input files:', e);
          }
          try {
            // Pass entries directly to onSubmit - platform doesn't transform anything
            await onSubmit(data.entries || []);
          } catch (err) {
            iframeRef.current?.contentWindow?.postMessage({
              type: 'llambada:error',
              code: ERROR_CODES.SUBMIT_FAILED,
              message: err instanceof Error ? err.message : 'Submit failed',
            }, '*');
          }
          break;

        case 'llambada:jsError': {
          const newError = {
            type: data.errorType,
            message: data.message,
            stack: data.stack,
            source: data.source,
            lineno: data.lineno,
            colno: data.colno,
            timestamp: data.timestamp
          };
          setErrors(prev => [...prev, newError]);
          setShowErrors(true);
          
          // Call onFrontendError callback if provided
          if (onFrontendError) {
            let errorText = `[${data.errorType?.toUpperCase() || 'ERROR'}] ${data.message}`;
            if (data.source) {
              errorText += `\n  at ${data.source}`;
              if (data.lineno) {
                errorText += `:${data.lineno}`;
                if (data.colno) errorText += `:${data.colno}`;
              }
            }
            if (data.stack) {
              errorText += `\n\nStack trace:\n${data.stack}`;
            }
            onFrontendError(errorText);
          }
          break;
        }

        case 'llambada:showToast':
          showHostToast(data.message, data.tone, data.duration);
          if (typeof data.requestId === 'string') {
            const iframeWindow = iframeRef.current?.contentWindow;
            if (e.source === iframeWindow && iframeWindow) {
              iframeWindow.postMessage({
                type: 'llambada:toastAck',
                requestId: data.requestId,
              }, '*');
            }
          }
          break;

        case 'llambada:runtimeReady': {
          const iframeWindow = iframeRef.current?.contentWindow;
          if (e.source === iframeWindow && iframeWindow) {
            iframeWindow.postMessage({ type: 'llambada:toastHostReady' }, '*');
            iframeWindow.postMessage({ type: 'llambada:measureLayout' }, '*');
            resolveIframeReady();
          }
          break;
        }

        case 'llambada:layoutMetrics': {
          const iframeWindow = iframeRef.current?.contentWindow;
          if (e.source === iframeWindow && iframeWindow) {
            applyIframeHeight(Number(data.height), data.debug && typeof data.debug === 'object' ? data.debug : null);
          }
          break;
        }

        case 'llambada:pendingRequests':
          pendingRequestsRef.current = data.count;
          if (data.count > 0) {
            window.addEventListener('beforeunload', handleBeforeUnload);
          } else {
            window.removeEventListener('beforeunload', handleBeforeUnload);
          }
          break;

        case 'llambada:getAuthToken':
          // Iframe is requesting auth token for data storage API
          // Use async callback (refreshes token) if available; else fall back to ref/localStorage
          (async () => {
            try {
              let token: string | null = null;
              if (getSessionTokenRef.current) {
                token = await getSessionTokenRef.current();
              }
              if (!token) {
                token = sessionTokenRef.current || getStoredSession()?.accessToken || null;
              }
              iframeRef.current?.contentWindow?.postMessage({
                type: 'llambada:authToken',
                token
              }, '*');
            } catch (err) {
              console.error('Failed to get auth token:', err);
              iframeRef.current?.contentWindow?.postMessage({
                type: 'llambada:authToken',
                token: null
              }, '*');
            }
          })();
          break;

        case 'llambada:getTurnstileToken':
          (async () => {
            try {
              const token = getTurnstileTokenRef.current
                ? await getTurnstileTokenRef.current()
                : null;
              iframeRef.current?.contentWindow?.postMessage({
                type: 'llambada:turnstileToken',
                token
              }, '*');
            } catch (err) {
              console.error('Failed to get Turnstile token:', err);
              iframeRef.current?.contentWindow?.postMessage({
                type: 'llambada:turnstileToken',
                token: null
              }, '*');
            }
          })();
          break;

        case 'llambada:logUnknownError': {
          // Iframe encountered an error code not in its catalog — log it to the database
          const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
          if (supabaseUrl) {
            try {
              const session = getStoredSession();
              const headers: Record<string, string> = { 'Content-Type': 'application/json' };
              if (session?.accessToken) {
                headers['Authorization'] = `Bearer ${session.accessToken}`;
              }
              fetch(`${supabaseUrl}/functions/v1/log-platform-error`, {
                method: 'POST',
                headers,
                body: JSON.stringify({
                  errorCode: data.errorCode || 'UNKNOWN',
                  errorMessage: data.errorMessage || '',
                  errorDetails: data.errorDetails || {},
                  projectId: projectId || null,
                  source: 'iframe',
                  userAgent: navigator.userAgent,
                }),
              }).catch((err) => console.error('Failed to log unknown error:', err));
            } catch (err) {
              console.error('Failed to log unknown error:', err);
            }
          }
          break;
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [onSubmit, buildStatus, publicMode, onFrontendError, resolveIframeReady, applyIframeHeight]);

  // Sync a file to the iframe's in-memory store.
  // Also cache it in the parent so we can re-sync after an iframe rebuild.
  const syncFileToIframe = async (path: string, data: ArrayBuffer) => {
    // Cache for tab-recovery: clone the buffer so it survives even if the
    // caller's ArrayBuffer is detached / transferred.
    cachedOutputFilesRef.current.set(path, data.slice(0));
    if (path !== 'progress.json') {
      const otherFiles = lastOutputFilesMetaRef.current.filter((file) => file.filename !== path);
      lastOutputFilesMetaRef.current = [...otherFiles, { filename: path, size: data.byteLength }];
    }

    console.log('[Llambada] syncFileToIframe called:', path, 'size:', data.byteLength);
    return new Promise<void>((resolve, reject) => {
      const timeoutMs = data.byteLength > 16 * 1024 * 1024 ? 60000 : 10000;
      const timeout = setTimeout(() => {
        console.error('[Llambada] Sync timeout for:', path);
        window.removeEventListener('message', handler);
        reject(new Error('Sync timeout'));
      }, timeoutMs);
      
      const handler = (e: MessageEvent) => {
        if (e.data?.type === 'llambada:syncComplete' && e.data.path === path) {
          console.log('[Llambada] Sync complete:', path);
          clearTimeout(timeout);
          window.removeEventListener('message', handler);
          resolve();
        }
        if (e.data?.type === 'llambada:syncError' && e.data.path === path) {
          clearTimeout(timeout);
          window.removeEventListener('message', handler);
          reject(new Error(e.data.error));
        }
      };
      
      window.addEventListener('message', handler);

      const transferableBuffer = data.slice(0);
      try {
        iframeRef.current?.contentWindow?.postMessage({
          type: 'llambada:syncFile',
          path,
          buffer: transferableBuffer,
        }, '*', [transferableBuffer]);
      } catch {
        iframeRef.current?.contentWindow?.postMessage({
          type: 'llambada:syncFile',
          path,
          data: Array.from(new Uint8Array(data)),
        }, '*');
      }
    });
  };

  // Expose sync function for parent to use
  useEffect(() => {
    (window as any).__llambadaSyncFile = syncFileToIframe;
    
    // Register started callback - notifies iframe when job starts so it can clear files
    (window as any).__llambadaStarted = () => {
      // Clear cached files from previous job (in-memory + IndexedDB)
      // Note: input files are NOT cleared here — they were already cached during
      // submit and will be needed for recovery even after the new job starts.
      cachedOutputFilesRef.current.clear();
      lastOutputFilesMetaRef.current = [];
      if (projectId) clearCachedRun(projectId).catch(() => {});
      iframeRef.current?.contentWindow?.postMessage({
        type: 'llambada:started',
      }, '*');
    };
    
    // Register progress callback
    (window as any).__llambadaSyncProgress = (data: { percent: number; message: string }) => {
      iframeRef.current?.contentWindow?.postMessage({
        type: 'llambada:progress',
        percent: data.percent,
        message: data.message,
      }, '*');
    };
    
    // Register complete callback
    (window as any).__llambadaComplete = (outputFiles: Array<{ filename: string; size: number }>) => {
      (window as any).__llambadaCompleteFired = true;
      lastOutputFilesMetaRef.current = outputFiles;
      iframeRef.current?.contentWindow?.postMessage({
        type: 'llambada:complete',
        outputFiles,
      }, '*');
      // Persist output files (and input files) to IndexedDB so they survive full page reload (tab discard)
      if (projectId && cachedOutputFilesRef.current.size > 0) {
        const files: Array<{ path: string; data: ArrayBuffer }> = [];
        for (const [path, data] of cachedOutputFilesRef.current.entries()) {
          files.push({ path, data: data.slice(0) });
        }
        const inputFiles: Array<{ path: string; data: ArrayBuffer; contentType: string }> = [];
        for (const [path, entry] of cachedInputFilesRef.current.entries()) {
          inputFiles.push({ path, data: entry.data.slice(0), contentType: entry.contentType });
        }
        saveCachedRun({
          projectId,
          files,
          inputFiles,
          outputFilesMeta: outputFiles,
          isComplete: true,
          error: null,
          timestamp: Date.now(),
        }).catch(() => {});
      }
    };

    // Register error callback (accepts structured PlatformError objects)
    (window as any).__llambadaError = (error: PlatformError | string) => {
      // Support both structured errors and legacy string errors
      const errorObj: PlatformError = typeof error === 'string'
        ? { code: ERROR_CODES.UNKNOWN, message: error }
        : error;
      iframeRef.current?.contentWindow?.postMessage({
        type: 'llambada:error',
        code: errorObj.code,
        message: errorObj.message,
        details: errorObj.details || {},
      }, '*');
      // Persist error state to IndexedDB so it survives tab discard
      if (projectId) {
        const files: Array<{ path: string; data: ArrayBuffer }> = [];
        for (const [path, data] of cachedOutputFilesRef.current.entries()) {
          files.push({ path, data: data.slice(0) });
        }
        const inputFiles: Array<{ path: string; data: ArrayBuffer; contentType: string }> = [];
        for (const [path, entry] of cachedInputFilesRef.current.entries()) {
          inputFiles.push({ path, data: entry.data.slice(0), contentType: entry.contentType });
        }
        saveCachedRun({
          projectId,
          files,
          inputFiles,
          outputFilesMeta: lastOutputFilesMetaRef.current,
          isComplete: false,
          error: errorObj.message,
          timestamp: Date.now(),
        }).catch(() => {});
      }
    };
    
    return () => {
      delete (window as any).__llambadaSyncFile;
      delete (window as any).__llambadaSyncProgress;
      delete (window as any).__llambadaStarted;
      delete (window as any).__llambadaComplete;
      delete (window as any).__llambadaError;
    };
  }, []);

  // Send completion to iframe when processing finishes.
  // Skip if __llambadaComplete callback already fired (avoid duplicate dispatch).
  useEffect(() => {
    if (isComplete && !isProcessing) {
      if ((window as any).__llambadaCompleteFired) {
        (window as any).__llambadaCompleteFired = false;
        return;
      }
      iframeRef.current?.contentWindow?.postMessage({
        type: 'llambada:complete',
        outputFiles: [],
      }, '*');
    }
  }, [isComplete, isProcessing]);

  // (Visibility-change state re-push is handled by the unified tab-recovery
  // effect above — no separate handler needed.)

  // ── Restore cached output files from IndexedDB after a full page reload ──
  // When the browser discards a tab, all in-memory state is lost. On reload,
  // check IndexedDB for output files from a previous completed run and re-sync
  // them to the freshly loaded iframe so the user sees their results.
  const restoredFromCacheRef = useRef(false);
  useEffect(() => {
    if (!iframeLoaded || !projectId || restoredFromCacheRef.current) return;
    // Only attempt restore if the current run is idle (no active processing).
    // If isProcessing is true, a live run is in progress — don't overwrite it.
    if (isProcessing) return;
    // If we already have the run mirrored in memory, there is nothing to restore.
    if (cachedOutputFilesRef.current.size > 0 || lastOutputFilesMetaRef.current.length > 0) {
      restoredFromCacheRef.current = true;
      return;
    }

    restoredFromCacheRef.current = true;

    (async () => {
      const cached = await loadCachedRun(projectId);
      if (!cached) return;

      console.log('[FrontendPreview] Restoring', cached.files.length, 'output files from IndexedDB cache');

      // Populate the in-memory cache so the visibility-change handler also has them
      for (const { path, data } of cached.files) {
        cachedOutputFilesRef.current.set(path, data);
      }
      lastOutputFilesMetaRef.current = cached.outputFilesMeta;

      // Restore input files to in-memory cache
      if (cached.inputFiles) {
        for (const { path, data, contentType } of cached.inputFiles) {
          cachedInputFilesRef.current.set(path, { data, contentType });
        }
      }

      // Restore input files to in-memory cache (for visibility-change recovery)
      if (cached.inputFiles) {
        for (const { path, data, contentType } of cached.inputFiles) {
          cachedInputFilesRef.current.set(path, { data, contentType });
        }
      }

      // Sync output files to the iframe
      for (const { path, data } of cached.files) {
        try {
          await syncFileToIframe(path, data);
        } catch (err) {
          console.error('[FrontendPreview] Failed to restore file from cache:', path, err);
        }
      }

      // Send the completion or error event
      const iframeWin = iframeRef.current?.contentWindow;
      if (!iframeWin) return;

      // Serialize input file data so the runtime can restore broken media elements
      const inputFileData: Array<{ filename: string; data: number[]; contentType: string }> = [];
      if (cached.inputFiles) {
        for (const { path, data, contentType } of cached.inputFiles) {
          inputFileData.push({ filename: path, data: Array.from(new Uint8Array(data)), contentType });
        }
      }

      if (cached.isComplete) {
        iframeWin.postMessage({
          type: 'llambada:complete',
          outputFiles: cached.outputFilesMeta,
          __inputFileData: inputFileData,
          restored: true,
        }, '*');
      } else if (cached.error) {
        iframeWin.postMessage({
          type: 'llambada:error',
          code: 'EXECUTION_FAILED',
          message: cached.error,
          restored: true,
        }, '*');
      }
    })().catch((err) => {
      console.error('[FrontendPreview] Cache restore failed:', err);
    });
  }, [iframeLoaded, projectId, isProcessing]);

  // Notify iframe when build status changes
  useEffect(() => {
    iframeRef.current?.contentWindow?.postMessage({
      type: 'llambada:buildStatus',
      isBuilding: buildStatus === 'building',
    }, '*');
  }, [buildStatus]);

  // Auto-show errors when backendError appears
  useEffect(() => {
    if (backendError) {
      setShowErrors(true);
    }
  }, [backendError]);

  // Public mode: render only the iframe with dynamic height
  const toastToneClassName = hostToast?.tone === 'success'
    ? 'bg-theme-success-bg border-theme-success-border text-theme-success'
    : hostToast?.tone === 'error'
      ? 'bg-theme-error-bg border-theme-error-border text-theme-error'
      : hostToast?.tone === 'warning'
        ? 'bg-theme-warning-bg border-theme-warning-border text-theme-warning'
        : 'bg-theme-surface-elevated border-theme-border text-theme-text';

  const hostToastNode = hostToast ? (
    <div className="pointer-events-none fixed inset-x-0 top-4 z-[9999] flex justify-center px-4">
      <div className={`max-w-[min(92vw,640px)] rounded-lg border px-4 py-2 text-sm shadow-lg backdrop-blur-sm ${toastToneClassName}`}>
        {hostToast.message}
      </div>
    </div>
  ) : null;

  const handleIframeLoad = useCallback(() => {
    // Wait for the injected runtime to signal readiness before treating the
    // iframe as available for cache restore or recovery syncs.
  }, []);

  if (publicMode) {
    return (
      <div className="w-full relative">
        <iframe
          ref={iframeRef}
          key={key}
          className="w-full bg-white"
          style={{
            height: `${iframeHeight}px`,
          }}
          sandbox="allow-scripts allow-forms allow-downloads allow-modals allow-popups allow-popups-to-escape-sandbox"
          title="Mini App"
          onLoad={handleIframeLoad}
        />
        {/* Recovery overlay — covers potentially broken iframe while rebuilding */}
        {isRecovering && (
          <div className="absolute inset-0 bg-white flex items-center justify-center z-10">
            <Loader2 className="h-6 w-6 text-gray-400 animate-spin" />
          </div>
        )}
        {hostToastNode}
      </div>
    );
  }

  return (
    <div className={`relative ${isExpanded ? 'fixed inset-2 sm:inset-4 z-50' : ''}`}>
      {isExpanded && (
        <div
          className="absolute inset-0 bg-theme-overlay-heavy -z-10"
          onClick={() => setIsExpanded(false)}
        />
      )}
      <div
        ref={containerRef}
        className={`bg-theme-bg rounded-xl border border-theme-border-muted overflow-hidden ${
          isExpanded ? 'h-full flex flex-col' : ''
        }`}
      >
        {/* Toolbar */}
        <div className="flex items-center justify-between px-3 sm:px-4 py-2 bg-theme-surface border-b border-theme-border-muted">
          <div className="flex items-center gap-2">
            <Play className="h-4 w-4 text-accent-400" />
            <span className="text-xs sm:text-sm font-medium text-theme-text-secondary">Preview</span>
            {totalErrors > 0 && (
              <button
                onClick={() => setShowErrors(!showErrors)}
                className="flex items-center gap-1 px-1.5 sm:px-2 py-0.5 bg-theme-error-bg text-theme-error rounded text-[10px] sm:text-xs font-medium hover:opacity-80 transition-colors"
              >
                <AlertTriangle className="h-3 w-3" />
                <span className="hidden sm:inline">{totalErrors} error{totalErrors > 1 ? 's' : ''}</span>
                <span className="sm:hidden">{totalErrors}</span>
              </button>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={handleRefresh}
              className="p-1.5 rounded-lg text-theme-text-muted hover:text-theme-text hover:bg-theme-surface-hover transition-colors"
              title="Refresh"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-1.5 rounded-lg text-theme-text-muted hover:text-theme-text hover:bg-theme-surface-hover transition-colors"
              title={isExpanded ? 'Minimize' : 'Expand'}
            >
              {isExpanded ? (
                <Minimize2 className="h-4 w-4" />
              ) : (
                <Maximize2 className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>

        {/* Error Panel */}
        {showErrors && totalErrors > 0 && (
          <div className="border-b border-theme-border-muted bg-theme-error-bg max-h-64 overflow-auto">
            <div className="flex items-center justify-between px-4 py-2 border-b border-theme-error-border sticky top-0 bg-theme-error-bg backdrop-blur-sm">
              <span className="text-sm font-medium text-theme-error">Errors</span>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleCopyErrors}
                  className="flex items-center gap-1 px-2 py-1 text-xs bg-theme-surface-hover hover:bg-theme-surface-active text-theme-text-secondary rounded transition-colors"
                  title="Copy all errors to clipboard"
                >
                  {copied ? (
                    <>
                      <Check className="h-3 w-3 text-theme-success" />
                      <span className="text-theme-success">Copied!</span>
                    </>
                  ) : (
                    <>
                      <Copy className="h-3 w-3" />
                      <span>Copy for AI</span>
                    </>
                  )}
                </button>
                <button
                  onClick={() => setShowErrors(false)}
                  className="p-1 text-theme-text-muted hover:text-theme-text rounded transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="p-3 space-y-3">
              {/* Backend error */}
              {backendError && (
                <div className="bg-theme-error-bg border border-theme-error-border rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-medium text-theme-error bg-theme-error-bg px-1.5 py-0.5 rounded border border-theme-error-border">BACKEND</span>
                  </div>
                  <pre className="text-xs text-theme-error-muted font-mono whitespace-pre-wrap break-all">{backendError}</pre>
                </div>
              )}
              {/* Frontend errors */}
              {errors.map((error, i) => (
                <div key={i} className="bg-theme-warning-bg border border-theme-warning-border rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-medium text-theme-warning bg-theme-warning-bg px-1.5 py-0.5 rounded border border-theme-warning-border">FRONTEND</span>
                    <span className="text-xs text-theme-text-muted">
                      {error.source && `${error.source.split('/').pop()}`}
                      {error.lineno && `:${error.lineno}`}
                      {error.colno && `:${error.colno}`}
                    </span>
                  </div>
                  <p className="text-sm text-theme-error font-medium mb-1">{error.message}</p>
                  {error.stack && (
                    <details className="mt-2">
                      <summary className="text-xs text-theme-text-muted cursor-pointer hover:text-theme-text-secondary">Stack trace</summary>
                      <pre className="mt-1 text-xs text-theme-text-muted font-mono whitespace-pre-wrap break-all overflow-x-auto">{error.stack}</pre>
                    </details>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* iframe with frozen overlay when building or blocked */}
        <div
          className={`relative ${isExpanded ? 'flex-1' : ''}`}
        >
          <iframe
            ref={iframeRef}
            key={key}
            className="w-full bg-white"
            style={{
              height: isExpanded ? '100%' : `${iframeHeight}px`,
            }}
            // Allow downloads from within the sandboxed mini-app iframe.
            // The default mini-app includes a "Download" link for output files;
            // without this token, browsers block the download.
            sandbox="allow-scripts allow-forms allow-downloads allow-modals allow-popups allow-popups-to-escape-sandbox"
            title="Frontend Preview"
            onLoad={handleIframeLoad}
          />
          
          {/* Loading overlay - shown while iframe is loading */}
          {!iframeLoaded && !isRecovering && !isBlocked && buildStatus !== 'building' && buildStatus !== 'pending' && (
            <div className="absolute inset-0 bg-theme-surface flex items-center justify-center">
              <div className="text-center">
                <Loader2 className="h-8 w-8 text-theme-text-muted animate-spin mx-auto mb-3" />
                <p className="text-theme-text-muted text-sm">Loading the mini-app...</p>
              </div>
            </div>
          )}

          {/* Recovery overlay — covers potentially broken iframe while rebuilding after tab return */}
          {isRecovering && (
            <div className="absolute inset-0 bg-white flex items-center justify-center z-10">
              <Loader2 className="h-6 w-6 text-gray-400 animate-spin" />
            </div>
          )}

          {/* Blocked overlay - shown when container needs rebuild due to dependency changes */}
          {isBlocked && !buildStatus && (
            <div className="absolute inset-0 bg-theme-overlay backdrop-blur-sm flex items-center justify-center cursor-not-allowed">
              <div className="bg-theme-surface-elevated border border-theme-warning-border rounded-lg p-6 text-center max-w-sm mx-4" style={{ boxShadow: 'var(--shadow-xl)' }}>
                <AlertTriangle className="h-8 w-8 text-theme-warning mx-auto mb-3" />
                <p className="text-theme-warning font-medium mb-1">Preview Unavailable</p>
                <p className="text-theme-text-muted text-sm">{blockedMessage}</p>
              </div>
            </div>
          )}
          
          {/* Frozen overlay when building or pending */}
          {(buildStatus === 'building' || buildStatus === 'pending') && (
            <div className="absolute inset-0 bg-theme-overlay backdrop-blur-sm flex items-center justify-center cursor-not-allowed">
              <div className="bg-theme-surface-elevated border border-theme-warning-border rounded-lg p-6 text-center max-w-sm mx-4" style={{ boxShadow: 'var(--shadow-xl)' }}>
                <Loader2 className="h-8 w-8 text-theme-warning animate-spin mx-auto mb-3" />
                <p className="text-theme-warning font-medium mb-1">
                  {buildStatus === 'pending' ? 'Starting Build...' : 'Rebuilding Backend'}
                </p>
                <p className="text-theme-text-muted text-sm">
                  {buildStatus === 'pending' 
                    ? 'Preparing to rebuild the backend container.'
                    : 'The mini-app will be available once the build completes (1-2 minutes).'}
                </p>
              </div>
            </div>
          )}
        </div>
        
        {/* Logs Panel Footer */}
        <div className="border-t border-theme-border-muted">
          {/* Logs Header / Toggle */}
          <button
            onClick={() => setShowLogs(!showLogs)}
            className="w-full flex items-center justify-between px-3 sm:px-4 py-2 bg-theme-surface hover:bg-theme-surface-hover transition-colors"
          >
            <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
              <Terminal className="h-4 w-4 text-theme-text-muted shrink-0" />
              <span className="text-xs sm:text-sm font-medium text-theme-text-secondary">Logs</span>
              
              {/* Status indicators */}
              <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                {/* Build status */}
                {(lastBuildId || buildStatus === 'pending') && (
                  <span className={`text-[10px] sm:text-xs px-1 sm:px-1.5 py-0.5 rounded flex items-center gap-1 ${
                    buildStatus === 'failed' 
                      ? 'bg-theme-error-bg text-theme-error' 
                      : buildStatus === 'ready'
                      ? 'bg-theme-success-bg text-theme-success'
                      : (buildStatus === 'building' || buildStatus === 'pending')
                      ? 'bg-theme-warning-bg text-theme-warning'
                      : 'bg-theme-surface-active text-theme-text-muted'
                  }`}>
                    {(buildStatus === 'building' || buildStatus === 'pending') && (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    )}
                    <span className="hidden sm:inline">Build: </span>
                    {buildStatus === 'pending' ? 'starting...' : (buildStatus || '?')}
                  </span>
                )}
                
                {/* Redeploy button - shown when build failed (not during pending/building) */}
                {buildStatus === 'failed' && onTriggerRebuild && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onTriggerRebuild();
                    }}
                    className="p-1 rounded bg-theme-error-bg hover:bg-theme-error/20 text-theme-error transition-colors"
                    title="Retry deployment"
                  >
                    <RefreshCw className="h-3 w-3" />
                  </button>
                )}
                
                {/* Run status */}
                {(runStatus === 'running' || runStatus === 'authenticating') && (
                  <span className="text-[10px] sm:text-xs px-1 sm:px-1.5 py-0.5 rounded bg-theme-info-bg text-theme-info flex items-center gap-1">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    <span className="hidden sm:inline">Run: {runStatus === 'authenticating' ? 'connecting' : 'running'}</span>
                  </span>
                )}
                {runLogs && (runStatus === 'completed' || runStatus === 'failed' || runStatus === 'idle') && (
                  <span className={`text-[10px] sm:text-xs px-1 sm:px-1.5 py-0.5 rounded ${
                    (runLogs.exitCode !== 0 || runLogs.hasAppError)
                      ? 'bg-theme-error-bg text-theme-error' 
                      : 'bg-theme-success-bg text-theme-success'
                  }`}>
                    <span className="hidden sm:inline">Run: </span>{(runLogs.exitCode === 0 && !runLogs.hasAppError) ? 'ok' : (runLogs.exitCode !== 0 ? `exit ${runLogs.exitCode}` : 'err')}
                  </span>
                )}
              </div>
            </div>
            
            {showLogs ? (
              <ChevronDown className="h-4 w-4 text-theme-text-muted" />
            ) : (
              <ChevronUp className="h-4 w-4 text-theme-text-muted" />
            )}
          </button>
          
          {/* Logs Content */}
          {showLogs && (
            <div className="bg-theme-bg">
              {/* Tabs */}
              <div className="flex border-b border-theme-border-muted">
                <button
                  onClick={() => {
                    setActiveLogTab('deployment');
                    if (!buildLogs && !buildLogsLoading && lastBuildId && onFetchBuildLogs) {
                      onFetchBuildLogs();
                    }
                  }}
                  className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium transition-colors ${
                    activeLogTab === 'deployment'
                      ? 'text-theme-text border-b-2 border-accent-400 bg-theme-surface'
                      : 'text-theme-text-muted hover:text-theme-text-secondary'
                  }`}
                >
                  <span className="hidden sm:inline">Deployment</span>
                  <span className="sm:hidden">Deploy</span>
                  {buildStatus === 'failed' && (
                    <span className="ml-1 sm:ml-2 w-2 h-2 rounded-full bg-theme-error inline-block" />
                  )}
                </button>
                <button
                  onClick={() => setActiveLogTab('backend')}
                  className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium transition-colors ${
                    activeLogTab === 'backend'
                      ? 'text-theme-text border-b-2 border-accent-400 bg-theme-surface'
                      : 'text-theme-text-muted hover:text-theme-text-secondary'
                  }`}
                >
                  Backend
                  {runLogs && (runLogs.exitCode !== 0 || runLogs.hasAppError) && (
                    <span className="ml-1 sm:ml-2 w-2 h-2 rounded-full bg-theme-error inline-block" />
                  )}
                </button>
                <button
                  onClick={() => setActiveLogTab('frontend')}
                  className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium transition-colors ${
                    activeLogTab === 'frontend'
                      ? 'text-theme-text border-b-2 border-accent-400 bg-theme-surface'
                      : 'text-theme-text-muted hover:text-theme-text-secondary'
                  }`}
                >
                  Frontend
                  {errors.length > 0 && (
                    <span className="ml-1 sm:ml-2 w-2 h-2 rounded-full bg-theme-error inline-block" />
                  )}
                </button>
              </div>
              
              {/* Tab Content */}
              <div className="max-h-48 sm:max-h-64 overflow-y-auto" ref={(el) => {
                // Auto-scroll to bottom when logs update during building or backend running
                if (el && (buildStatus === 'building' || ((runStatus === 'running' || runStatus === 'authenticating') && activeLogTab === 'backend'))) {
                  el.scrollTop = el.scrollHeight;
                }
              }}>
                {activeLogTab === 'deployment' && (
                  <div className="p-3">
                    {buildStatus === 'building' && (
                      <div className="flex items-center gap-2 text-theme-warning mb-3">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span className="text-sm font-medium">Building...</span>
                      </div>
                    )}
                    {isImageSizeLimitError && buildStatus === 'failed' && (
                      <div className="mb-3 p-3 rounded-lg bg-theme-error-bg border border-theme-error/20">
                        <div className="flex items-center gap-2 text-theme-error mb-2">
                          <AlertTriangle className="h-4 w-4" />
                          <span className="text-sm font-medium">Docker Image Size Limit Exceeded</span>
                        </div>
                        <p className="text-xs text-theme-text-muted">
                          Your image exceeds the 500MB limit. Use a smaller base image or remove unnecessary dependencies.
                        </p>
                      </div>
                    )}
                    {lastBuildAt && buildStatus !== 'building' && (
                      <div className="text-xs text-theme-text-muted mb-2">
                        Last build: {new Date(lastBuildAt).toLocaleString()}
                      </div>
                    )}
                    {buildLogsLoading && !buildLogs && (
                      <div className="flex items-center gap-2 text-theme-text-muted py-4">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading build logs...
                      </div>
                    )}
                    {buildLogsError && (
                      <div className="text-theme-error text-sm py-2">{buildLogsError}</div>
                    )}
                    {buildLogs && (
                      <pre className={`text-xs font-mono whitespace-pre-wrap break-all ${
                        buildStatus === 'failed' ? 'text-theme-error' : 'text-theme-text-secondary'
                      }`}>{buildLogs}</pre>
                    )}
                    {/* Diagnostics section - shows how logs were fetched */}
                    {buildLogsDiagnostics && (
                      <details className="mt-4 border-t border-theme-border-muted pt-3">
                        <summary className="text-xs text-theme-text-muted cursor-pointer hover:text-theme-text-secondary">
                          🔍 Log Fetch Diagnostics
                        </summary>
                        <div className="mt-2 p-2 bg-theme-surface rounded text-xs font-mono space-y-1">
                          {buildLogsDiagnostics.steps.map((step, i) => (
                            <div key={i} className="text-theme-text-secondary whitespace-pre-wrap">{step}</div>
                          ))}
                          {buildLogsDiagnostics.logsBucket && (
                            <div className="mt-2 pt-2 border-t border-theme-border-muted">
                              <span className="text-theme-text-muted">Logs Bucket: </span>
                              <span className="text-theme-text-secondary">{buildLogsDiagnostics.logsBucket}</span>
                            </div>
                          )}
                          {buildLogsDiagnostics.gcsError && (
                            <div className="text-theme-warning">
                              GCS Error: {buildLogsDiagnostics.gcsError}
                            </div>
                          )}
                          {buildLogsDiagnostics.cloudLoggingError && (
                            <div className="text-theme-warning">
                              Cloud Logging Error: {buildLogsDiagnostics.cloudLoggingError}
                            </div>
                          )}
                        </div>
                      </details>
                    )}
                    {!buildLogsLoading && !buildLogsError && !buildLogs && !lastBuildId && buildStatus !== 'building' && (
                      <p className="text-theme-text-muted text-sm">No build history available.</p>
                    )}
                    {!buildLogsLoading && !buildLogsError && !buildLogs && lastBuildId && buildStatus !== 'building' && (
                      <p className="text-theme-text-muted text-sm">Click to load build logs.</p>
                    )}
                    {buildStatus === 'building' && !buildLogs && !buildLogsLoading && !buildLogsError && (
                      <p className="text-theme-text-muted text-sm">Waiting for logs...</p>
                    )}
                  </div>
                )}
                
                {activeLogTab === 'backend' && (
                  <div className="p-3">
                    {/* Streaming console output while running */}
                    {(runStatus === 'authenticating' || runStatus === 'running') && (
                      <div>
                        {/* Console output — primary content */}
                        {streamingLogs && (streamingLogs.stdout || streamingLogs.stderr) ? (
                          <div>
                            {streamingLogs.stdout && (
                              <pre className="text-xs font-mono text-theme-text-secondary whitespace-pre-wrap break-all">{streamingLogs.stdout}</pre>
                            )}
                            {streamingLogs.stderr && (
                              <pre className="text-xs font-mono text-theme-error whitespace-pre-wrap break-all">{streamingLogs.stderr}</pre>
                            )}
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 text-theme-text-muted">
                            <Loader2 className="h-3 w-3 animate-spin" />
                            <span className="text-xs">
                              {runStatus === 'authenticating' ? 'Connecting...' : 'Waiting for output...'}
                            </span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Show final logs after completion or failure */}
                    {runLogs && (runStatus === 'completed' || runStatus === 'failed') && (
                      <div>
                        {runLogs.stdout && (
                          <pre className="text-xs font-mono text-theme-text-secondary whitespace-pre-wrap break-all">{runLogs.stdout}</pre>
                        )}
                        {runLogs.stderr && (
                          <pre className="text-xs font-mono text-theme-error whitespace-pre-wrap break-all">{runLogs.stderr}</pre>
                        )}
                        {!runLogs.stdout && !runLogs.stderr && backendError && (
                          <pre className="text-xs font-mono text-theme-error whitespace-pre-wrap break-all">{backendError}</pre>
                        )}
                        {!runLogs.stdout && !runLogs.stderr && !backendError && (
                          <p className="text-theme-text-muted text-sm">No output captured.</p>
                        )}
                        {runLogs.exitCode !== 0 && (
                          <div className="text-xs text-theme-error mt-1">Exit code: {runLogs.exitCode}</div>
                        )}
                      </div>
                    )}

                    {/* Show error when failed but no runLogs available yet */}
                    {runStatus === 'failed' && !runLogs && backendError && (
                      <pre className="text-xs font-mono text-theme-error whitespace-pre-wrap break-all">{backendError}</pre>
                    )}

                    {/* Show idle state */}
                    {runStatus === 'idle' && !runLogs && (
                      <p className="text-theme-text-muted text-sm">No backend logs yet. Submit the form to run the backend.</p>
                    )}
                  </div>
                )}
                
                {activeLogTab === 'frontend' && (
                  <div className="p-3">
                    {errors.length > 0 ? (
                      <div className="space-y-3">
                        {errors.map((error, index) => (
                          <div key={error.timestamp + index} className="border-b border-theme-border-muted pb-3 last:border-b-0 last:pb-0">
                            <div className="flex items-start gap-2 mb-1">
                              <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-theme-error-bg text-theme-error">
                                {error.type === 'unhandledrejection' ? 'Promise Rejection' : 'Error'}
                              </span>
                              <span className="text-xs text-theme-text-muted">
                                {new Date(error.timestamp).toLocaleTimeString()}
                              </span>
                            </div>
                            <pre className="text-xs font-mono text-theme-error whitespace-pre-wrap break-all mb-1">
                              {error.message}
                            </pre>
                            {error.source && (
                              <div className="text-xs text-theme-text-muted">
                                at {error.source}
                                {error.lineno && `:${error.lineno}`}
                                {error.colno && `:${error.colno}`}
                              </div>
                            )}
                            {error.stack && (
                              <details className="mt-2">
                                <summary className="text-xs text-theme-text-muted cursor-pointer hover:text-theme-text-secondary">
                                  Stack trace
                                </summary>
                                <pre className="text-xs font-mono text-theme-text-muted whitespace-pre-wrap break-all mt-1 pl-2 border-l-2 border-theme-border-muted">
                                  {error.stack}
                                </pre>
                              </details>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-theme-text-muted text-sm">No frontend errors captured.</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      {hostToastNode}
    </div>
  );
}
